/**
 * RenderPort implementation: Canvas 2D.
 * Dark tower-defense theme with split layout: grid + HUD panel + inventory bar.
 */

import type { RenderPort, GameStateView, MapProgressView } from "../../application/ports.js";
import { MAP_COLS, MAP_ROWS } from "../../application/global-map-fields.js";
import {
  getGameGridLayout,
  getMapGridLayout,
  getMapHexCircumradius,
  cellToCanvas,
  cellToIsoCanvas,
  pointToIsoCell,
  type GridLayout,
} from "./grid-math.js";
import { t } from "../../i18n/index.js";
import { activeCampaign } from "../../campaign/index.js";
import { resolveCampaignVisualForField } from "../../campaign/types.js";
import type { CampaignConfig, CampaignVisualConfig } from "../../campaign/index.js";
import type { BuildingType, MonsterType } from "../../domain/types.js";

const DEFAULT_ROWS = 13;
const DEFAULT_COLS = 13;

// Helper to route between orthographic 2D top-down and 2.5D Isometric projection
function cellPos(row: number, col: number, layout: GridLayout): { x: number; y: number } {
  if (activeCampaign.ui?.theme === "modern") {
    return cellToIsoCanvas(row, col, layout);
  }
  return cellPos(row, col, layout);
}


// === Palette — "Good vs Evil" theme (docs/design/theme-good-vs-evil.md) ===
const BG = "#1a1612";           // parchment base / dark wood
const GAME_GRID_LINE = "rgba(96, 78, 64, 0.18)"; // subtle on tiles — less "wire box"
const HUD_BG = "#0a0806";       // charcoal panel
const TEXT_PRI = "#e8dcb4";     // parchment cream
const TEXT_SEC = "#8a7a5a";     // faded ink
const START_CLR = "#5c1a1a";    // dark red (rift)
const END_CLR = "#d4af37";      // gold (altar)
const VICTORY_CLR = "#d4af37";  // gold — SANCTIFIED
const DEFEAT_CLR = "#5c1a1a";   // dark red — FALLEN
const SEPARATOR = "#3a2820";    // aged wood

const GEM_COLOR: Record<string, string> = {
  Yellow: "#ffff00",
  Orange: "#ff8800",
  Red: "#ff0000",
  Purple: "#aa00aa",
  Green: "#00ff00",
  Blue: "#0000ff",
  Cyan: "#00ffff",
  Lime: "#88ff00",
};

// --- Color Utility for Mixed Gems (B13) ---
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 0, g: 0, b: 0 };
}

function rgbToHex(r: number, g: number, b: number): string {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

function getGemColor(type: string, secondaryType?: string): string {
  const c1 = GEM_COLOR[type] ?? "#888888";
  if (!secondaryType) return c1;
  const c2 = GEM_COLOR[secondaryType] ?? "#888888";
  const rgb1 = hexToRgb(c1);
  const rgb2 = hexToRgb(c2);
  return rgbToHex(
    Math.round((rgb1.r + rgb2.r) / 2),
    Math.round((rgb1.g + rgb2.g) / 2),
    Math.round((rgb1.b + rgb2.b) / 2)
  );
}

// --- Campaign visual manifest assets ---
const buildingImgs = new Map<BuildingType, HTMLImageElement>();
const monsterSheetImgs = new Map<MonsterType, HTMLImageElement>();
const monsterFallbackImgs = new Map<MonsterType, HTMLImageElement>();
const storyImgs = new Map<string, HTMLImageElement>();
const mapDecorationImgs: HTMLImageElement[] = [];
let mapBackgroundImg: HTMLImageElement | null = null;
let pathStartStripImg: HTMLImageElement | null = null;
let pathStartFallbackImg: HTMLImageElement | null = null;
let pathEndImg: HTMLImageElement | null = null;

// --- Battle field terrain tiles (seamless textures; one or more variants per role) ---
let tilePathImgs: HTMLImageElement[] = [];   // cobble — walkable
let tileBuildableImgs: HTMLImageElement[] = []; // open ground — buildable
let tileForestImgs: HTMLImageElement[] = []; // dark forest — blocked mix
let tileRocksImgs: HTMLImageElement[] = [];  // stone — blocked mix
let tilePathVerticalImg: HTMLImageElement | null = null;
let tilePathCornerNeImg: HTMLImageElement | null = null;
let tilePathCornerEsImg: HTMLImageElement | null = null;
let tilePathTJunctionImg: HTMLImageElement | null = null;
let tilePathCrossImg: HTMLImageElement | null = null;
let tilePathEndImg: HTMLImageElement | null = null;


const RIFT_STRIP_MS_PER_FRAME = 55;

type BuildingBattleVisual = NonNullable<CampaignVisualConfig["buildings"][BuildingType]["battle"]>;
type EndpointBattleVisual = NonNullable<NonNullable<CampaignVisualConfig["pathEndpoints"]["battle"]>["start"]>;

const DEFAULT_BUILDING_BATTLE_VISUAL: Record<BuildingType, Required<BuildingBattleVisual>> = {
  Tower: { scaleX: 1, scaleY: 1, anchorY: 0.56, haloColor: "rgba(212, 175, 55, 0.18)", haloScale: 1, shadowAlpha: 0.34 },
  Wall: { scaleX: 1, scaleY: 1, anchorY: 0.52, haloColor: "rgba(0, 0, 0, 0)", haloScale: 0.85, shadowAlpha: 0.28 },
  Amplifier: { scaleX: 1, scaleY: 1, anchorY: 0.55, haloColor: "rgba(212, 175, 55, 0.16)", haloScale: 1.2, shadowAlpha: 0.28 },
  Trap: { scaleX: 1, scaleY: 1, anchorY: 0.5, haloColor: "rgba(212, 175, 55, 0.12)", haloScale: 0.85, shadowAlpha: 0.18 },
  Shrine: { scaleX: 1, scaleY: 1, anchorY: 0.55, haloColor: "rgba(212, 175, 55, 0.2)", haloScale: 1.05, shadowAlpha: 0.3 },
};

const DEFAULT_ENDPOINT_BATTLE_VISUAL = {
  start: {
    scale: 1,
    glowScale: 1,
    glowColor: "rgba(255, 72, 28, 0.22)",
    ringColor: "rgba(255, 118, 42, 0.54)",
    particleColor: "rgba(255, 122, 48, 0.7)",
  },
  end: {
    scale: 1,
    glowScale: 1,
    glowColor: "rgba(120, 190, 255, 0.24)",
    ringColor: "rgba(212, 175, 55, 0.52)",
    particleColor: "rgba(255, 236, 170, 0.64)",
  },
} satisfies Record<"start" | "end", Required<EndpointBattleVisual>>;

function buildingBattleVisual(visual: CampaignVisualConfig, type: BuildingType): Required<BuildingBattleVisual> {
  return {
    ...DEFAULT_BUILDING_BATTLE_VISUAL[type],
    ...visual.buildings[type].battle,
  };
}

function endpointBattleVisual(
  visual: CampaignVisualConfig,
  endpoint: "start" | "end"
): Required<EndpointBattleVisual> {
  return {
    ...DEFAULT_ENDPOINT_BATTLE_VISUAL[endpoint],
    ...visual.pathEndpoints.battle?.[endpoint],
  };
}

export function getMonsterVisual(
  type: MonsterType,
  visual: CampaignVisualConfig = activeCampaign.visual
): CampaignVisualConfig["monsters"][MonsterType] {
  return visual.monsters[type] ?? visual.monsters.Normal;
}

type MapDecorationCleanStrategy =
  | { kind: "chroma"; tolerance: number }
  | { kind: "leonardo" }
  | null;

export function getMapDecorationCleanStrategy(
  decorationSrc: string,
  visual: CampaignVisualConfig = activeCampaign.visual
): MapDecorationCleanStrategy {
  for (const type of Object.keys(visual.monsters) as MonsterType[]) {
    const monsterVisual = getMonsterVisual(type, visual);
    if (monsterVisual.fallbackSrc !== decorationSrc) continue;
    return type === "Normal" || type === "Runner"
      ? { kind: "chroma", tolerance: 16 }
      : { kind: "leonardo" };
  }
  return null;
}

export function getStoryImageSrc(imageSrc: string, visual: CampaignVisualConfig = activeCampaign.visual): string {
  return visual.story.imageAliases[imageSrc] ?? imageSrc;
}

function addVisualManifestImageSources(sources: Set<string>, visual: CampaignVisualConfig): void {
  sources.add(visual.map.backgroundSrc);
  for (const decoration of visual.map.decorations) sources.add(decoration.imageSrc);
  for (const building of Object.values(visual.buildings)) sources.add(building.spriteSrc);
  sources.add(visual.pathEndpoints.startStripSrc);
  sources.add(visual.pathEndpoints.startFallbackSrc);
  sources.add(visual.pathEndpoints.endSrc);
  for (const monster of Object.values(visual.monsters)) {
    sources.add(monster.sheetSrc);
    sources.add(monster.fallbackSrc);
  }
  for (const storySrc of Object.values(visual.story.imageAliases)) sources.add(storySrc);
}

export function getCanvasManifestImageSources(visual: CampaignVisualConfig = activeCampaign.visual): string[] {
  const sources = new Set<string>();
  addVisualManifestImageSources(sources, visual);
  for (const fieldId of Object.keys(visual.fieldVisuals ?? {})) {
    addVisualManifestImageSources(sources, resolveCampaignVisualForField(visual, fieldId));
  }
  return [...sources];
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => {
      const fallbackSrc = getCommonAssetFallbackSrc(src);
      if (!fallbackSrc) {
        resolve(null);
        return;
      }
      const fallbackImg = new Image();
      fallbackImg.onload = () => resolve(fallbackImg);
      fallbackImg.onerror = () => resolve(null);
      fallbackImg.src = fallbackSrc;
    };
    img.src = src;
  });
}

export function getCommonAssetFallbackSrc(src: string): string | null {
  const terrainMatch = /^(.*)\/campaigns\/[^/]+\/terrain\/[^/]+\/([^/]+\.png)$/.exec(src);
  if (terrainMatch) return `${terrainMatch[1]}/common/terrain/${terrainMatch[2]}`;
  const match = /^(.*)\/campaigns\/[^/]+\/([^/]+)$/.exec(src);
  return match ? `${match[1]}/common/${match[2]}` : null;
}

function getBattleTileBasePath(base: string): string {
  return base.startsWith("/") ? base : `/tiles/${base}`;
}

function getBattleTileVariantSources(base: string): string[] {
  const basePath = getBattleTileBasePath(base);
  return [`${basePath}.png`, `${basePath}_2.png`, `${basePath}_3.png`, `${basePath}_4.png`];
}

function getBattleTileNamedSource(base: string, name: string): string {
  const basePath = base.startsWith("/") ? base : `/tiles/${base}`;
  return `${basePath}_${name}.png`;
}

/** Load `base.png`, optional `base_2.png`, `base_3.png`, `base_4.png` (missing files skipped). */
async function loadBattleTileVariants(base: string): Promise<HTMLImageElement[]> {
  const urls = getBattleTileVariantSources(base);
  const loaded = await Promise.all(urls.map((u) => loadImage(u)));
  return loaded.filter((img): img is HTMLImageElement => img !== null);
}

function battleTerrainReady(): boolean {
  return (
    tilePathImgs.length > 0 &&
    tileBuildableImgs.length > 0 &&
    tileForestImgs.length > 0 &&
    tileRocksImgs.length > 0
  );
}

/** FNV-1a–style seed from field id so terrain decoration is stable per map, different across maps. */
function fieldIdToTerrainSeed(fieldId: string | undefined | null): number {
  if (!fieldId) return 0;
  let h = 2166136261 >>> 0;
  for (let i = 0; i < fieldId.length; i++) {
    h ^= fieldId.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

/**
 * Stable pseudo-random uint32 per cell.
 * `mapSeed` = per-field salt (same field → same pattern each session).
 * `channel` decorrelates uses (forest vs rock pick vs texture index).
 */
function cellRngU32(row: number, col: number, channel: number, mapSeed: number): number {
  let h =
    (Math.imul(row + channel * 0x9e37_79b1, 0x85eb_ca6b) ^
      Math.imul(col + channel * 0xb529_7a4d, 0xc2b2_ae35) ^
      Math.imul(mapSeed + channel * 0x51_7cc1b7, 0x7feb_352d)) >>>
    0;
  h ^= h >>> 16;
  h = Math.imul(h, 0x27d4_eb2d);
  h ^= h >>> 15;
  h = Math.imul(h, 0x1656_67b1);
  return h >>> 0;
}

/**
 * Remove Leonardo-AI checker-pattern background from PNG assets.
 * Leonardo's "transparent" PNG export keeps a visible two-tone grey diagonal
 * checker as a preview artifact. We find the two most frequent grey tones
 * (R≈G≈B with small spread) via a quick histogram, then zero out any grey
 * pixel within tolerance of those tones. Colored silhouette pixels are
 * untouched because they fail the R≈G≈B test.
 */
async function leonardoAlphaClean(img: HTMLImageElement): Promise<HTMLImageElement> {
  // Offscreen canvas hits GPU/browser limits (~8192–16384px per side).
  const MAX_CLEAN_DIM = 8192;
  if (img.naturalWidth > MAX_CLEAN_DIM || img.naturalHeight > MAX_CLEAN_DIM) {
    return img;
  }
  const c = document.createElement("canvas");
  c.width = img.naturalWidth;
  c.height = img.naturalHeight;
  const cctx = c.getContext("2d");
  if (!cctx) return img;
  cctx.drawImage(img, 0, 0);
  const data = cctx.getImageData(0, 0, c.width, c.height);
  const px = data.data;

  // 1. Build histogram of grey tones (|R-G|<6, |G-B|<6) bucketed by brightness.
  const histogram = new Array<number>(256).fill(0);
  for (let i = 0; i < px.length; i += 4) {
    const r = px[i], g = px[i + 1], b = px[i + 2];
    if (Math.abs(r - g) < 6 && Math.abs(g - b) < 6 && Math.abs(r - b) < 6) {
      histogram[r]++;
    }
  }
  // Pick top-2 brightness peaks (non-adjacent) as checker tones.
  const peaks: number[] = [];
  const used = new Array<boolean>(256).fill(false);
  for (let pick = 0; pick < 2; pick++) {
    let best = -1, bestCount = 0;
    for (let v = 0; v < 256; v++) {
      if (used[v]) continue;
      if (histogram[v] > bestCount) { bestCount = histogram[v]; best = v; }
    }
    if (best < 0 || bestCount < (c.width * c.height * 0.02)) break;
    peaks.push(best);
    // Mask out a window of ±12 around this peak so the two picks aren't adjacent.
    for (let v = Math.max(0, best - 12); v <= Math.min(255, best + 12); v++) used[v] = true;
  }
  if (peaks.length === 0) return img; // No dominant grey — likely already clean.

  // 2. Zero alpha for any grey pixel close to one of the checker peaks (tolerance 18).
  const tol = 18;
  for (let i = 0; i < px.length; i += 4) {
    const r = px[i], g = px[i + 1], b = px[i + 2];
    if (Math.abs(r - g) >= 8 || Math.abs(g - b) >= 8 || Math.abs(r - b) >= 8) continue;
    for (const peak of peaks) {
      if (Math.abs(r - peak) <= tol) { px[i + 3] = 0; break; }
    }
  }
  cctx.putImageData(data, 0, 0);
  return new Promise((resolve) => {
    const out = new Image();
    out.onload = () => resolve(out);
    out.onerror = () => resolve(img);
    out.src = c.toDataURL();
  });
}

/**
 * Chroma-key the corner color of a sprite sheet to transparent so it can
 * overlay the dark game background cleanly. Tolerance is tight (~12 RGB
 * units) to preserve the slightly-dark mob silhouette.
 */
function chromaKey(img: HTMLImageElement, tolerance = 12): Promise<HTMLImageElement | null> {
  const c = document.createElement("canvas");
  c.width = img.naturalWidth;
  c.height = img.naturalHeight;
  const cctx = c.getContext("2d");
  if (!cctx) return Promise.resolve(null);
  cctx.drawImage(img, 0, 0);
  const data = cctx.getImageData(0, 0, c.width, c.height);
  const px = data.data;
  const r0 = px[0], g0 = px[1], b0 = px[2];
  const t2 = tolerance * tolerance * 3;
  for (let i = 0; i < px.length; i += 4) {
    const dr = px[i] - r0, dg = px[i + 1] - g0, db = px[i + 2] - b0;
    if (dr * dr + dg * dg + db * db <= t2) px[i + 3] = 0;
  }
  cctx.putImageData(data, 0, 0);
  return new Promise((resolve) => {
    const out = new Image();
    out.onload = () => resolve(out);
    out.onerror = () => resolve(null);
    out.src = c.toDataURL();
  });
}

async function preloadSprites(visual: CampaignVisualConfig): Promise<void> {
  const imageEntries = await Promise.all(
    getCanvasManifestImageSources(visual).map(async (src) => [src, await loadImage(src)] as const)
  );
  const images = new Map<string, HTMLImageElement>();
  for (const [src, img] of imageEntries) {
    if (img) images.set(src, img);
  }

  const cleanLeonardo = async (src: string): Promise<HTMLImageElement | null> => {
    const img = images.get(src) ?? null;
    return img ? leonardoAlphaClean(img) : null;
  };
  const cleanChroma = async (src: string, tolerance: number): Promise<HTMLImageElement | null> => {
    const img = images.get(src) ?? null;
    return img ? chromaKey(img, tolerance) : null;
  };
  const setIfLoaded = <TKey,>(registry: Map<TKey, HTMLImageElement>, key: TKey, img: HTMLImageElement | null) => {
    if (img) registry.set(key, img);
  };

  buildingImgs.clear();
  setIfLoaded(buildingImgs, "Tower", await cleanLeonardo(visual.buildings.Tower.spriteSrc));
  setIfLoaded(buildingImgs, "Wall", await cleanLeonardo(visual.buildings.Wall.spriteSrc));
  setIfLoaded(buildingImgs, "Amplifier", await cleanChroma(visual.buildings.Amplifier.spriteSrc, 32));
  setIfLoaded(buildingImgs, "Trap", await cleanChroma(visual.buildings.Trap.spriteSrc, 40));
  setIfLoaded(buildingImgs, "Shrine", await cleanChroma(visual.buildings.Shrine.spriteSrc, 32));

  mapBackgroundImg = images.get(visual.map.backgroundSrc) ?? null;
  mapDecorationImgs.splice(0, mapDecorationImgs.length);
  for (const decoration of visual.map.decorations) {
    const img = images.get(decoration.imageSrc) ?? null;
    if (!img) continue;
    const strategy = getMapDecorationCleanStrategy(decoration.imageSrc, visual);
    if (strategy?.kind === "chroma") {
      const cleaned = await cleanChroma(decoration.imageSrc, strategy.tolerance);
      if (cleaned) mapDecorationImgs.push(cleaned);
    } else if (strategy?.kind === "leonardo") {
      const cleaned = await cleanLeonardo(decoration.imageSrc);
      if (cleaned) mapDecorationImgs.push(cleaned);
    } else {
      mapDecorationImgs.push(img);
    }
  }

  const riftStrip = images.get(visual.pathEndpoints.startStripSrc) ?? null;
  if (riftStrip && riftStrip.complete) {
    const cleaned = await chromaKey(riftStrip, 14);
    if (cleaned) {
      const fw = cleaned.naturalHeight;
      const n = fw > 0 ? Math.floor(cleaned.naturalWidth / fw) : 0;
      pathStartStripImg = n >= 2 ? cleaned : null;
    } else {
      pathStartStripImg = null;
    }
  } else {
    pathStartStripImg = null;
  }
  pathStartFallbackImg = await cleanLeonardo(visual.pathEndpoints.startFallbackSrc);
  pathEndImg = await cleanLeonardo(visual.pathEndpoints.endSrc);

  monsterSheetImgs.clear();
  monsterFallbackImgs.clear();
  for (const type of Object.keys(visual.monsters) as MonsterType[]) {
    const monsterVisual = getMonsterVisual(type, visual);
    setIfLoaded(monsterSheetImgs, type, images.get(monsterVisual.sheetSrc) ?? null);
    const fallback = type === "Normal" || type === "Runner"
      ? await cleanChroma(monsterVisual.fallbackSrc, 16)
      : await cleanLeonardo(monsterVisual.fallbackSrc);
    setIfLoaded(monsterFallbackImgs, type, fallback);
  }

  const tileBases = visual.terrain.tileBases;
  const [
    pathTiles,
    buildableTiles,
    forestTiles,
    rocksTiles,
    pathVertical,
    pathCornerNe,
    pathCornerEs,
    pathTJunction,
    pathCross,
    pathEnd,
  ] = await Promise.all([
    loadBattleTileVariants(tileBases.path),
    loadBattleTileVariants(tileBases.buildable),
    loadBattleTileVariants(tileBases.forest),
    loadBattleTileVariants(tileBases.rocks),
    loadImage(getBattleTileNamedSource(tileBases.path, "vertical")),
    loadImage(getBattleTileNamedSource(tileBases.path, "corner_ne")),
    loadImage(getBattleTileNamedSource(tileBases.path, "corner_es")),
    loadImage(getBattleTileNamedSource(tileBases.path, "t")),
    loadImage(getBattleTileNamedSource(tileBases.path, "cross")),
    loadImage(getBattleTileNamedSource(tileBases.path, "end")),
  ]);
  tilePathImgs = pathTiles;
  tileBuildableImgs = buildableTiles;
  tileForestImgs = forestTiles;
  tileRocksImgs = rocksTiles;
  tilePathVerticalImg = pathVertical;
  tilePathCornerNeImg = pathCornerNe;
  tilePathCornerEsImg = pathCornerEs;
  tilePathTJunctionImg = pathTJunction;
  tilePathCrossImg = pathCross;
  tilePathEndImg = pathEnd;

  storyImgs.clear();
  for (const src of Object.values(visual.story.imageAliases)) {
    const img = images.get(src) ?? null;
    if (img) storyImgs.set(src, img);
  }
}

export interface CanvasRenderAdapterOptions {
  mountTarget: HTMLElement;
  canvas?: HTMLCanvasElement | null;
  width?: number;
  height?: number;
  campaign?: CampaignConfig;
}

/** Cell currently under the cursor in game scene (for build-ghost preview). */
let _hoverCell: { row: number; col: number } | null = null;
/** DnD: gem being dragged + cursor pos (persists across tick-driven re-renders). */
let _dragState: { gem: { type: string; level: number }; screenX: number; screenY: number } | null = null;
/** Current session elapsed ms — used by drawTowers for shrine charge progress. */
let drawTowersStateElapsedMs: number = 0;

/** Achievement-unlocked toasts. Rendered in top-left, 3s life each. */
interface Toast { title: string; subtitle?: string; startMs: number }
const _toasts: Toast[] = [];
const TOAST_DURATION_MS = 3500;

// ----------------------------------------------------------------
//  Visual effects — render-side transient state (B5)
// ----------------------------------------------------------------
interface DeathEffect { row: number; col: number; startMs: number }
const _deathEffects: DeathEffect[] = [];
/** monsterId → startMs of the banish-respawn halo */
const _respawnEffects: Map<string, number> = new Map();
/** previous-frame snapshot of monster positions for diffing */
const _prevMonsters: Map<string, { row: number; col: number }> = new Map();

const DEATH_DURATION_MS = 600;
const RESPAWN_DURATION_MS = 2000;
/** Cell-distance threshold to detect banish teleport (vs. normal movement) */
const BANISH_JUMP_THRESHOLD = 2.0;

// ----------------------------------------------------------------
//  Cell-flash feedback (B20) — placement error flash
// ----------------------------------------------------------------
interface CellFlash { row: number; col: number; color: string; startMs: number; durationMs: number }
const _cellFlashes: CellFlash[] = [];

// Tutorial step is now tracked in composition.ts and injected into GameStateView via Proxy.

export function createCanvasRenderAdapter(
  options: CanvasRenderAdapterOptions
): RenderPort {
  const campaign = options.campaign ?? activeCampaign;
  const visual = campaign.visual;
  const width = options.width ?? 800;
  const height = options.height ?? 600;
  const canvas =
    options.canvas ??
    (() => {
      const c = document.createElement("canvas");
      c.width = width;
      c.height = height;
      options.mountTarget.appendChild(c);
      return c;
    })();
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");

  // ── B7: Zoom state ────────────────────────────────────────────────────────
  const ZOOM_MIN = 0.5;
  const ZOOM_MAX = 3.0;
  let zoomScale = 1;
  let zoomOffsetX = 0;
  let zoomOffsetY = 0;

  /** Clamp offset so bitmap edges never leave the viewport. */
  function clampZoomOffset(scale: number, ox: number, oy: number): { ox: number; oy: number } {
    // At scale > 1 the scaled bitmap is larger than canvas; ox must be ≤ 0.
    // At scale < 1 the scaled bitmap is smaller; center it (offset > 0 allowed).
    const maxOx = Math.max(0, (canvas.width  - canvas.width  * scale) / 2);
    const minOx = Math.min(0, canvas.width  - canvas.width  * scale);
    const maxOy = Math.max(0, (canvas.height - canvas.height * scale) / 2);
    const minOy = Math.min(0, canvas.height - canvas.height * scale);
    return {
      ox: Math.max(minOx, Math.min(maxOx, ox)),
      oy: Math.max(minOy, Math.min(maxOy, oy)),
    };
  }
  // ─────────────────────────────────────────────────────────────────────────

  let lastState: GameStateView | null = null;
  let loadedVisualKey = "";

  function visualForState(state: GameStateView): CampaignVisualConfig {
    if (state.scene === "game") return resolveCampaignVisualForField(visual, state.field?.id);
    if (state.scene === "settings") return resolveCampaignVisualForField(visual, state.settings?.fieldId);
    return visual;
  }

  function visualKey(nextVisual: CampaignVisualConfig): string {
    return [
      ...getCanvasManifestImageSources(nextVisual),
      ...Object.values(nextVisual.terrain.tileBases),
    ].join("\n");
  }

  function ensureVisualPreloaded(nextVisual: CampaignVisualConfig): void {
    const nextKey = visualKey(nextVisual);
    if (nextKey === loadedVisualKey) return;
    loadedVisualKey = nextKey;
    void preloadSprites(nextVisual).then(() => { if (lastState) adapter.update(lastState); });
  }

  ensureVisualPreloaded(visual);

  const adapter: RenderPort = {
    setScene(_sceneId) {},
    update(state: GameStateView) {
      lastState = state;
      if (!ctx) return;
      const sceneVisual = visualForState(state);
      ensureVisualPreloaded(sceneVisual);

      // --- B5: diff monsters to detect deaths and banish-respawns ---
      if (state.scene === "game") {
        _updateVisualEffects(state);
      }

      // B7: clear the full canvas first (outside transform so no gaps at edges)
      ctx.fillStyle = BG;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Apply zoom transform for all scene draws
      ctx.save();
      ctx.setTransform(zoomScale, 0, 0, zoomScale, zoomOffsetX, zoomOffsetY);

      if (state.scene === "map") {
        renderMapScene(ctx, width, height, state, campaign);
      } else if (state.scene === "game") {
        renderGameScene(ctx, width, height, state, sceneVisual);
      } else if (state.scene === "settings") {
        // Settings scene: canvas shows a plain dark background;
        // the settings panel is rendered by the DOM HUD overlay (Phase 3).
        ctx.fillStyle = BG;
        ctx.fillRect(0, 0, width, height);
      } else if (state.scene === "editor") {
        renderEditorScene(ctx, width, height, state);
      } else if (state.scene === "story") {
        renderStoryScene(ctx, width, height, state, sceneVisual);
      } else {
        ctx.fillStyle = TEXT_PRI;
        ctx.font = "16px sans-serif";
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        ctx.fillText(`Scene: ${state.scene}`, 20, 30);
      }

      ctx.restore();

      // Achievement toasts drawn after restore so they are always at screen coords
      drawAchievementToasts(ctx, width, height);
    },

    // ── B7: Zoom API ─────────────────────────────────────────────────────────
    setZoom(scale: number, anchorX: number, anchorY: number): void {
      const newScale = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, scale));
      // Anchor: the canvas-bitmap point (anchorX, anchorY) must map to the same
      // screen pixel before and after the scale change.
      // Before: screenX = anchorX * zoomScale + zoomOffsetX
      // After:  screenX = anchorX * newScale  + newOffsetX  → newOffsetX = screenX - anchorX * newScale
      const screenX = anchorX * zoomScale + zoomOffsetX;
      const screenY = anchorY * zoomScale + zoomOffsetY;
      const rawOx = screenX - anchorX * newScale;
      const rawOy = screenY - anchorY * newScale;
      const { ox, oy } = clampZoomOffset(newScale, rawOx, rawOy);
      zoomScale = newScale;
      zoomOffsetX = ox;
      zoomOffsetY = oy;
      if (lastState) adapter.update(lastState);
    },
    getZoom(): number {
      return zoomScale;
    },
    getZoomState(): { scale: number; offsetX: number; offsetY: number } {
      return { scale: zoomScale, offsetX: zoomOffsetX, offsetY: zoomOffsetY };
    },
    resetZoom(): void {
      zoomScale = 1;
      zoomOffsetX = 0;
      zoomOffsetY = 0;
      if (lastState) adapter.update(lastState);
    },
    // ─────────────────────────────────────────────────────────────────────────
    hitTestSkillButton(_x: number, _y: number): number | null { return null; },
    hitTestSkillsPanelClose(_x: number, _y: number): boolean { return false; },
    hitTestSkillsPanelButton(_x: number, _y: number): boolean { return false; },
    hitTestStatsPanelButton(_x: number, _y: number): boolean { return false; },
    hitTestStatsPanelClose(_x: number, _y: number): boolean { return false; },
    hitTestStageSettingOption(_x: number, _y: number): { kind: "hp" | "waves"; index: number } | null { return null; },
    hitTestStageSettingToggle(_x: number, _y: number): "extraBanishCost" | "healOnReturn" | null { return null; },
    hitTestStageSettingStart(_x: number, _y: number): boolean { return false; },
    hitTestStageSettingCancel(_x: number, _y: number): boolean { return false; },
    hitTestTutorialNext(_x: number, _y: number): boolean { return false; },
    hitTestTutorialSkip(_x: number, _y: number): boolean { return false; },
    hitTestStoryNext(_x: number, _y: number): boolean {
      if (lastState?.scene !== "story") return false;
      return true; // Click anywhere to advance story
    },
    setHoverCell(cell) {
      _hoverCell = cell;
      // Trigger re-render with the last state so the ghost follows smoothly.
      if (lastState && lastState.scene === "game") adapter.update(lastState);
    },
    setDragState(drag) {
      _dragState = drag;
      if (lastState && lastState.scene === "game") adapter.update(lastState);
    },
    showAchievementToast(title, subtitle) {
      _toasts.push({ title, subtitle, startMs: performance.now() });
      if (lastState) adapter.update(lastState);
    },
    flashCell(cell: { row: number; col: number }, color = "rgba(220, 60, 60, 0.55)", durationMs = 600): void {
      _cellFlashes.push({ row: cell.row, col: cell.col, color, startMs: performance.now(), durationMs });
      if (lastState) adapter.update(lastState);
    },
    setTutorialStep(_step: number): void {
      // Tutorial overlay is now in DOM HUD (Phase 3).
      // state.tutorialStep is injected by composition proxy on every update.
    },
  };
  return adapter;
}

// ================================================================
//  GAME SCENE
// ================================================================

function renderGameScene(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  state: GameStateView,
  visual: CampaignVisualConfig
): void {
  const rows = state.field?.rows ?? DEFAULT_ROWS;
  const cols = state.field?.cols ?? DEFAULT_COLS;
  const layout = getGameGridLayout(rows, cols);
  const terrainMapSeed = fieldIdToTerrainSeed(state.field?.id);

  drawBattleSceneBackdrop(ctx, width, height, layout, rows, cols);
  drawGameFieldBackdrop(
    ctx,
    rows,
    cols,
    layout,
    state.originalPathCells ?? state.pathCells,
    state.blockedCells,
    state.waypoints,
    terrainMapSeed
  );
  // Fallback procedural blocked cells only when terrain tiles haven't loaded yet.
  if (state.blockedCells?.length && !battleTerrainReady()) {
    drawBlockedCells(ctx, state.blockedCells, layout);
  }
  drawGameGrid(ctx, rows, cols, layout);
  drawBattleFieldVignette(ctx, layout, rows, cols);
  drawBattleFieldFrame(ctx, layout, rows, cols);

  if (state.waypoints?.length) {
    const animMs = state.elapsedMs ?? 0;
    drawPath(ctx, state.waypoints, state.pathCells, layout, animMs, terrainMapSeed, visual);
    drawPathEmbers(ctx, state.originalPathCells ?? state.pathCells, layout, animMs, terrainMapSeed);
  }

  if (state.towers?.length) {
    drawTowersStateElapsedMs = state.elapsedMs ?? 0;
    drawTowers(ctx, state.towers, layout, visual);
  }

  // Hover ghost preview for current placement mode
  drawHoverGhost(ctx, state, layout);

  // Tooltip with tower stats on hover
  drawTowerStats(ctx, state, layout);

  // B5: death smoke effects (drawn under live monsters)
  drawDeathEffects(ctx, layout);

  if (state.monsters?.length) {
    // B5: draw respawn halos behind monster sprites
    drawRespawnHalos(ctx, state.monsters, layout);
    drawMonsters(ctx, state.monsters, layout, visual);
  }

  if (state.shots?.length) {
    drawShots(ctx, state.shots, layout);
  }

  if (state.hitFlashes?.length) {
    drawHitFlashes(ctx, state.hitFlashes, layout);
  }

  // B20: placement-error cell flash (drawn above hit-flashes, below HUD)
  drawCellFlashes(ctx, layout);

  // drawInventory moved to DOM HUD (Phase 2.4)

  const drag = _dragState ?? state.dragState ?? null;
  if (drag) {
    drawGhostGem(ctx, drag);
  }

  // Summary overlay and tutorial overlay moved to DOM HUD (Phase 3).
}

// --- Battle field backdrop ---

function drawBattleSceneBackdrop(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  layout: GridLayout,
  rows: number,
  cols: number
): void {
  const isModern = activeCampaign.ui?.theme === "modern";
  const { cellSize, offsetX, offsetY } = layout;
  const gridW = cols * cellSize;
  const gridH = rows * cellSize;

  ctx.save();
  if (typeof ctx.createLinearGradient === "function") {
    const base = ctx.createLinearGradient(0, 0, width, height);
    if (isModern) {
      base.addColorStop(0, "#050914");
      base.addColorStop(0.46, "#0a1226");
      base.addColorStop(1, "#03050a");
    } else {
      base.addColorStop(0, "#100c09");
      base.addColorStop(0.46, "#21130d");
      base.addColorStop(1, "#080706");
    }
    ctx.fillStyle = base;
  } else {
    ctx.fillStyle = isModern ? "#070c18" : "#100c09";
  }
  ctx.fillRect(0, 0, width, height);

  ctx.globalAlpha = isModern ? 0.12 : 0.18;
  ctx.fillStyle = isModern ? "#00f0ff" : "#3a2418";
  const step = Math.max(18, cellSize * 0.5);
  for (let y = -step; y < height + step; y += step) {
    ctx.fillRect(0, y, width, 1);
  }
  for (let x = -step; x < width + step; x += step) {
    ctx.fillRect(x, 0, 1, height);
  }

  ctx.globalAlpha = 1;
  if (typeof ctx.createRadialGradient === "function") {
    const tableGlow = ctx.createRadialGradient(
      offsetX + gridW * 0.5,
      offsetY + gridH * 0.48,
      gridW * 0.18,
      offsetX + gridW * 0.5,
      offsetY + gridH * 0.48,
      Math.max(gridW, gridH) * 0.72
    );
    if (isModern) {
      tableGlow.addColorStop(0, "rgba(0, 240, 255, 0.18)");
      tableGlow.addColorStop(0.54, "rgba(10, 35, 75, 0.22)");
      tableGlow.addColorStop(1, "rgba(0, 0, 0, 0.55)");
    } else {
      tableGlow.addColorStop(0, "rgba(122, 58, 28, 0.24)");
      tableGlow.addColorStop(0.54, "rgba(45, 24, 15, 0.22)");
      tableGlow.addColorStop(1, "rgba(0, 0, 0, 0.36)");
    }
    ctx.fillStyle = tableGlow;
    ctx.fillRect(0, 0, width, height);
  }

  ctx.shadowColor = "rgba(0, 0, 0, 0.72)";
  ctx.shadowBlur = cellSize * 0.45;
  ctx.shadowOffsetY = cellSize * 0.1;
  ctx.fillStyle = "rgba(12, 8, 6, 0.62)";
  ctx.fillRect(
    offsetX - cellSize * 0.22,
    offsetY - cellSize * 0.22,
    gridW + cellSize * 0.44,
    gridH + cellSize * 0.44
  );
  ctx.restore();
}

function drawGameFieldBackdrop(
  ctx: CanvasRenderingContext2D,
  rows: number,
  cols: number,
  layout: GridLayout,
  pathCells: Array<{ row: number; col: number }> | undefined,
  blockedCells: Array<{ row: number; col: number }> | undefined,
  waypoints: Array<{ row: number; col: number }> | undefined,
  terrainMapSeed: number
): void {
  const { cellSize, offsetX, offsetY } = layout;
  const pathSet = new Set<string>();
  if (pathCells) for (const c of pathCells) pathSet.add(`${c.row},${c.col}`);
  // Portal / altar waypoints must read as road under sprites even if path DTOs lag.
  if (waypoints?.length) {
    pathSet.add(`${waypoints[0].row},${waypoints[0].col}`);
    const wLast = waypoints[waypoints.length - 1];
    pathSet.add(`${wLast.row},${wLast.col}`);
  }
  const blockedSet = new Set<string>();
  if (blockedCells) for (const c of blockedCells) blockedSet.add(`${c.row},${c.col}`);

  // Procedural base (shown until tiles load, or if assets missing).
  ctx.fillStyle = "rgba(24, 20, 17, 0.75)";
  ctx.fillRect(offsetX, offsetY, cols * cellSize, rows * cellSize);

  const allTilesReady = battleTerrainReady();
  if (!allTilesReady) {
    // Fallback: flat path colour (incl. portal / altar cells).
    if (pathCells && pathCells.length >= 2) {
      ctx.fillStyle = "rgba(156, 140, 118, 0.32)";
      for (const c of pathCells) {
        const { x, y } = cellPos(c.row, c.col, layout);
        ctx.fillRect(x - cellSize / 2, y - cellSize / 2, cellSize, cellSize);
      }
    }
    if (waypoints?.length) {
      ctx.fillStyle = "rgba(156, 140, 118, 0.36)";
      for (const w of [waypoints[0], waypoints[waypoints.length - 1]]) {
        const { x, y } = cellPos(w.row, w.col, layout);
        ctx.fillRect(x - cellSize / 2, y - cellSize / 2, cellSize, cellSize);
      }
    }
    return;
  }

  // One path / buildable texture per field — even color on road and buildable ground.
  const pathTileForField = tilePathImgs[terrainMapSeed % tilePathImgs.length]!;
  const buildableIdx =
    tileBuildableImgs.length > 0
      ? (Math.imul(terrainMapSeed ^ 0x9e37_79b9, 0x7feb_352d) >>> 0) % tileBuildableImgs.length
      : 0;
  const buildableTileForField = tileBuildableImgs[buildableIdx]!;
  // One blocked biome per field (forest XOR rocks) — kills harsh light-rock / dark-forest checker on obstacles.
  const isModern = activeCampaign.ui?.theme === "modern";
  if (isModern) {
    const tileW = cellSize * 1.35;
    const tileH = cellSize * 0.65;
    ctx.save();
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const k = `${r},${c}`;
        const { x, y } = cellPos(r, c, layout);
        ctx.beginPath();
        ctx.moveTo(x, y - tileH / 2);
        ctx.lineTo(x + tileW / 2, y);
        ctx.lineTo(x, y + tileH / 2);
        ctx.lineTo(x - tileW / 2, y);
        ctx.closePath();

        if (pathSet.has(k)) {
          ctx.fillStyle = "rgba(0, 180, 240, 0.35)";
          ctx.strokeStyle = "rgba(0, 240, 255, 0.7)";
          ctx.lineWidth = 1.5;
        } else if (blockedSet.has(k)) {
          ctx.fillStyle = "rgba(20, 15, 30, 0.85)";
          ctx.strokeStyle = "rgba(100, 80, 120, 0.4)";
          ctx.lineWidth = 1;
        } else {
          ctx.fillStyle = (r + c) % 2 === 0 ? "rgba(15, 25, 45, 0.65)" : "rgba(20, 32, 58, 0.65)";
          ctx.strokeStyle = "rgba(0, 240, 255, 0.18)";
          ctx.lineWidth = 0.8;
        }
        ctx.fill();
        ctx.stroke();
      }
    }
    ctx.restore();
    return;
  }
  const blockedIsForestBiome = ((terrainMapSeed >>> 5) & 1) === 0;
  const forestIdx =
    tileForestImgs.length > 0
      ? (Math.imul(terrainMapSeed ^ 0x2b99_629d, 0xb529_7a4d) >>> 0) % tileForestImgs.length
      : 0;
  const rockIdx =
    tileRocksImgs.length > 0
      ? (Math.imul(terrainMapSeed ^ 0x5133_66f1, 0x85eb_ca6b) >>> 0) % tileRocksImgs.length
      : 0;
  const blockedTileForField = blockedIsForestBiome
    ? tileForestImgs[forestIdx]!
    : tileRocksImgs[rockIdx]!;

  const prevSmoothing = ctx.imageSmoothingEnabled;
  ctx.imageSmoothingEnabled = false;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const k = `${r},${c}`;
      let img: HTMLImageElement;
      // Walkable path (incl. portal / altar cells) wins over authored obstacles — rift must sit on cobble.
      if (pathSet.has(k)) {
        const tile = selectPathTileForCell(pathSet, r, c, pathTileForField);
        drawTileCellTransformed(ctx, tile.img, r, c, layout, tile.turns);
        continue;
      } else if (blockedSet.has(k)) {
        img = blockedTileForField;
      } else {
        img = buildableTileForField;
      }
      drawTileCell(ctx, img, r, c, layout);
    }
  }
  ctx.imageSmoothingEnabled = prevSmoothing;

  // Buddha/common battle ground can contain strong build sigils in the tile art.
  // Darken open cells slightly so towers and monsters win visual priority.
  ctx.save();
  ctx.fillStyle = "rgba(8, 6, 5, 0.18)";
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const k = `${r},${c}`;
      if (pathSet.has(k) || blockedSet.has(k)) continue;
      const { x, y } = cellPos(r, c, layout);
      ctx.fillRect(x - cellSize / 2, y - cellSize / 2, cellSize, cellSize);
    }
  }
  ctx.restore();

  // Slight unified wash — pulls extreme highlights down so the battle board reads calmer.
  const fw = cols * cellSize;
  const fh = rows * cellSize;
  ctx.save();
  ctx.fillStyle = "rgba(22, 18, 15, 0.072)";
  ctx.fillRect(offsetX, offsetY, fw, fh);
  ctx.restore();
}

/** Subtle radial darkening at field edges — cheap depth cue without new art. */
function drawBattleFieldVignette(
  ctx: CanvasRenderingContext2D,
  layout: GridLayout,
  rows: number,
  cols: number
): void {
  if (typeof ctx.createRadialGradient !== "function") return;
  const { cellSize, offsetX, offsetY } = layout;
  const w = cols * cellSize;
  const h = rows * cellSize;
  if (w <= 0 || h <= 0) return;
  const cx = offsetX + w * 0.5;
  const cy = offsetY + h * 0.5;
  const radius = Math.hypot(w, h) * 0.58;
  const g = ctx.createRadialGradient(cx, cy, radius * 0.28, cx, cy, radius);
  g.addColorStop(0, "rgba(0, 0, 0, 0)");
  g.addColorStop(0.65, "rgba(6, 4, 10, 0.28)");
  g.addColorStop(1, "rgba(2, 1, 6, 0.62)");
  ctx.save();
  ctx.fillStyle = g;
  ctx.fillRect(offsetX, offsetY, w, h);
  ctx.restore();
}

/** Sparse animated embers on the monster road (stable placement per cell, cap for perf). */
function drawPathEmbers(
  ctx: CanvasRenderingContext2D,
  pathCells: Array<{ row: number; col: number }> | undefined,
  layout: GridLayout,
  animMs: number,
  mapSeed: number
): void {
  if (!pathCells || pathCells.length < 2) return;
  const { cellSize } = layout;
  const cap = 240;
  const step = pathCells.length > cap ? Math.ceil(pathCells.length / cap) : 1;
  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  for (let i = 0; i < pathCells.length; i += step) {
    const cell = pathCells[i]!;
    const h = cellRngU32(cell.row, cell.col, 44, mapSeed);
    if ((h >>> 5) % 3 === 0) continue;
    const { x, y } = cellPos(cell.row, cell.col, layout);
    const ox = (((h >>> 8) % 1001) / 1000 - 0.5) * cellSize * 0.72;
    const oy = (((h >>> 20) % 1001) / 1000 - 0.5) * cellSize * 0.72;
    const flicker = 0.28 + 0.22 * Math.sin(animMs * 0.0028 + (h & 127) * 0.03);
    const r = 1.0 + ((h >>> 4) & 7) * 0.22;
    ctx.beginPath();
    ctx.fillStyle = `rgba(255, 110, 48, ${flicker * 0.42})`;
    ctx.arc(x + ox, y + oy, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

/** Sample a cellSize×cellSize region from a seamless tile, stable per (row,col). */
function drawTileCell(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  row: number,
  col: number,
  layout: GridLayout
): void {
  const { cellSize, offsetX, offsetY } = layout;
  const iw = img.naturalWidth;
  const ih = img.naturalHeight;
  if (iw <= 0 || ih <= 0) return;
  // Tile source region scale: ~3 cells fit across one source image horizontally.
  const tileSpanCells = 3;
  const srcCellW = iw / tileSpanCells;
  const srcCellH = Math.min(ih, iw / tileSpanCells);
  const sx = (col * srcCellW) % Math.max(1, iw - srcCellW);
  const sy = (row * srcCellH) % Math.max(1, ih - srcCellH);
  // Round dx/dy to avoid sub-pixel gaps; ceil the draw size to overlap by 1px.
  const dx = Math.round(offsetX + col * cellSize);
  const dy = Math.round(offsetY + row * cellSize);
  const dw = Math.ceil(cellSize);
  const dh = Math.ceil(cellSize);
  ctx.drawImage(img, sx, sy, srcCellW, srcCellH, dx, dy, dw, dh);
}

function drawTileCellTransformed(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  row: number,
  col: number,
  layout: GridLayout,
  quarterTurns: 0 | 1 | 2 | 3
): void {
  if (quarterTurns === 0) {
    drawTileCell(ctx, img, row, col, layout);
    return;
  }
  const { cellSize, offsetX, offsetY } = layout;
  const iw = img.naturalWidth;
  const ih = img.naturalHeight;
  if (iw <= 0 || ih <= 0) return;
  const tileSpanCells = 3;
  const srcCellW = iw / tileSpanCells;
  const srcCellH = Math.min(ih, iw / tileSpanCells);
  const sx = (col * srcCellW) % Math.max(1, iw - srcCellW);
  const sy = (row * srcCellH) % Math.max(1, ih - srcCellH);
  const dx = Math.round(offsetX + col * cellSize);
  const dy = Math.round(offsetY + row * cellSize);
  const dw = Math.ceil(cellSize);
  const dh = Math.ceil(cellSize);
  ctx.save();
  ctx.translate(dx + dw / 2, dy + dh / 2);
  ctx.rotate((Math.PI / 2) * quarterTurns);
  ctx.drawImage(img, sx, sy, srcCellW, srcCellH, -dw / 2, -dh / 2, dw, dh);
  ctx.restore();
}

function pathHas(pathSet: Set<string>, row: number, col: number): boolean {
  return pathSet.has(`${row},${col}`);
}

function selectPathTileForCell(
  pathSet: Set<string>,
  row: number,
  col: number,
  fallback: HTMLImageElement
): { img: HTMLImageElement; turns: 0 | 1 | 2 | 3 } {
  const n = pathHas(pathSet, row - 1, col);
  const e = pathHas(pathSet, row, col + 1);
  const s = pathHas(pathSet, row + 1, col);
  const w = pathHas(pathSet, row, col - 1);
  const degree = Number(n) + Number(e) + Number(s) + Number(w);

  if (degree >= 4 && tilePathCrossImg) return { img: tilePathCrossImg, turns: 0 };
  if (degree === 3 && tilePathTJunctionImg) return { img: tilePathTJunctionImg, turns: 0 };

  if (degree === 1 && tilePathEndImg) {
    if (w) return { img: tilePathEndImg, turns: 0 };
    if (n) return { img: tilePathEndImg, turns: 1 };
    if (e) return { img: tilePathEndImg, turns: 2 };
    return { img: tilePathEndImg, turns: 3 };
  }

  if (n && s && !e && !w) {
    return tilePathVerticalImg ? { img: tilePathVerticalImg, turns: 0 } : { img: fallback, turns: 1 };
  }
  if (e && w && !n && !s) return { img: fallback, turns: 0 };

  if (n && e && tilePathCornerEsImg) return { img: tilePathCornerEsImg, turns: 2 };
  if (e && s && tilePathCornerNeImg) return { img: tilePathCornerNeImg, turns: 0 };
  if (s && w && tilePathCornerEsImg) return { img: tilePathCornerEsImg, turns: 0 };
  if (w && n && tilePathCornerNeImg) return { img: tilePathCornerNeImg, turns: 2 };

  return { img: fallback, turns: n || s ? 1 : 0 };
}

// --- Blocked cells (obstacles / rocks) ---

function drawBlockedCells(
  ctx: CanvasRenderingContext2D,
  cells: Array<{ row: number; col: number }>,
  layout: GridLayout
): void {
  const { cellSize } = layout;
  for (const cell of cells) {
    const { x, y } = cellPos(cell.row, cell.col, layout);
    const cx = x - cellSize / 2;
    const cy = y - cellSize / 2;
    ctx.fillStyle = "rgba(58, 46, 40, 0.95)";
    ctx.fillRect(cx, cy, cellSize, cellSize);
    ctx.strokeStyle = "rgba(20, 14, 10, 0.8)";
    ctx.lineWidth = 1;
    ctx.strokeRect(cx, cy, cellSize, cellSize);
  }
}

// --- Grid ---

function drawGameGrid(
  ctx: CanvasRenderingContext2D,
  rows: number,
  cols: number,
  layout: GridLayout
): void {
  // Isometric grid is drawn inside drawGameFieldBackdrop for modern theme
  if (activeCampaign.ui?.theme === "modern") return;

  const { cellSize, offsetX, offsetY } = layout;
  ctx.strokeStyle = GAME_GRID_LINE;
  ctx.lineWidth = 0.5;
  for (let r = 0; r <= rows; r++) {
    const y = offsetY + r * cellSize;
    ctx.beginPath();
    ctx.moveTo(offsetX, y);
    ctx.lineTo(offsetX + cols * cellSize, y);
    ctx.stroke();
  }
  for (let c = 0; c <= cols; c++) {
    const x = offsetX + c * cellSize;
    ctx.beginPath();
    ctx.moveTo(x, offsetY);
    ctx.lineTo(x, offsetY + rows * cellSize);
    ctx.stroke();
  }
}

function drawEndpointRing(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  cellSize: number,
  animMs: number,
  visual: Required<EndpointBattleVisual>,
  direction: 1 | -1
): void {
  const t = animMs * 0.0012 * direction;
  const ringR = cellSize * 0.58 * visual.scale;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(t);
  ctx.strokeStyle = visual.ringColor;
  ctx.lineWidth = Math.max(1.4, cellSize * 0.045);
  ctx.lineCap = "round";
  for (let i = 0; i < 3; i++) {
    const start = i * (Math.PI * 2 / 3) + Math.sin(animMs * 0.002 + i) * 0.12;
    ctx.beginPath();
    ctx.arc(0, 0, ringR + i * cellSize * 0.08, start, start + Math.PI * 0.72);
    ctx.stroke();
  }
  ctx.fillStyle = visual.particleColor;
  for (let i = 0; i < 5; i++) {
    const angle = t * 1.7 + i * (Math.PI * 2 / 5);
    const r = ringR * (0.72 + 0.28 * Math.sin(animMs * 0.0023 + i));
    ctx.beginPath();
    ctx.arc(Math.cos(angle) * r, Math.sin(angle) * r, Math.max(1, cellSize * 0.022), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function drawBattleFieldFrame(
  ctx: CanvasRenderingContext2D,
  layout: GridLayout,
  rows: number,
  cols: number
): void {
  const { cellSize, offsetX, offsetY } = layout;
  const w = cols * cellSize;
  const h = rows * cellSize;
  ctx.save();
  ctx.strokeStyle = "rgba(212, 175, 55, 0.20)";
  ctx.lineWidth = Math.max(1, cellSize * 0.018);
  ctx.strokeRect(offsetX + 0.5, offsetY + 0.5, w - 1, h - 1);
  ctx.strokeStyle = "rgba(0, 0, 0, 0.48)";
  ctx.lineWidth = Math.max(2, cellSize * 0.045);
  ctx.strokeRect(offsetX - 1, offsetY - 1, w + 2, h + 2);
  ctx.restore();
}

// --- Monster Path ---

function drawPath(
  ctx: CanvasRenderingContext2D,
  waypoints: Array<{ row: number; col: number }>,
  pathCells: Array<{ row: number; col: number }> | undefined,
  layout: GridLayout,
  /** Session clock for rift strip (advances with battle tick; pauses when speed is 0). */
  animMs: number,
  terrainMapSeed: number,
  visual: CampaignVisualConfig
): void {
  if (waypoints.length < 2) return;
  const { cellSize } = layout;
  // Use pathCells (BFS road) if available; fall back to direct waypoint segments.
  const pathPoints = pathCells && pathCells.length >= 2 ? pathCells : waypoints;

  const startPt = cellPos(waypoints[0].row, waypoints[0].col, layout);
  const endWp = waypoints[waypoints.length - 1];
  const endPt = cellPos(endWp.row, endWp.col, layout);
  const startVisual = endpointBattleVisual(visual, "start");
  const endVisual = endpointBattleVisual(visual, "end");

  // Ambient pools under portal / altar sprites (cheap radial gradients).
  if (typeof ctx.createRadialGradient === "function") {
    const pulseIn = 0.82 + 0.18 * Math.sin(animMs * 0.0034);
    const gIn = ctx.createRadialGradient(
      startPt.x,
      startPt.y,
      cellSize * 0.06,
      startPt.x,
      startPt.y,
      cellSize * 1.08 * startVisual.glowScale
    );
    gIn.addColorStop(0, startVisual.glowColor);
    gIn.addColorStop(0.35, "rgba(200, 50, 20, 0.12)");
    gIn.addColorStop(0.52, "rgba(110, 24, 12, 0.14)");
    gIn.addColorStop(1, "rgba(0, 0, 0, 0)");
    ctx.save();
    ctx.globalAlpha *= pulseIn;
    ctx.fillStyle = gIn;
    ctx.fillRect(
      startPt.x - cellSize * 1.15 * startVisual.glowScale,
      startPt.y - cellSize * 1.15 * startVisual.glowScale,
      cellSize * 2.3 * startVisual.glowScale,
      cellSize * 2.3 * startVisual.glowScale
    );
    ctx.restore();

    const pulseOut = 0.86 + 0.14 * Math.sin(animMs * 0.0026 + 1.1);
    const gOut = ctx.createRadialGradient(
      endPt.x,
      endPt.y,
      cellSize * 0.04,
      endPt.x,
      endPt.y,
      cellSize * 1.32 * endVisual.glowScale
    );
    gOut.addColorStop(0, endVisual.glowColor);
    gOut.addColorStop(0.42, "rgba(212, 175, 55, 0.2)");
    gOut.addColorStop(1, "rgba(0, 0, 0, 0)");
    ctx.save();
    ctx.globalAlpha *= pulseOut;
    ctx.fillStyle = gOut;
    ctx.fillRect(
      endPt.x - cellSize * 1.38 * endVisual.glowScale,
      endPt.y - cellSize * 1.38 * endVisual.glowScale,
      cellSize * 2.76 * endVisual.glowScale,
      cellSize * 2.76 * endVisual.glowScale
    );
    ctx.restore();
  }

  drawEndpointRing(ctx, startPt.x, startPt.y, cellSize, animMs, startVisual, 1);
  drawEndpointRing(ctx, endPt.x, endPt.y, cellSize, animMs + 730, endVisual, -1);

  // Cobble under portal / altar (radial glow sits above the first field pass; redraw so sprites read as on road).
  if (battleTerrainReady() && tilePathImgs.length > 0) {
    const pathImg = tilePathImgs[terrainMapSeed % tilePathImgs.length]!;
    const prevSmooth = ctx.imageSmoothingEnabled;
    ctx.imageSmoothingEnabled = false;
    drawTileCell(ctx, pathImg, waypoints[0].row, waypoints[0].col, layout);
    drawTileCell(ctx, pathImg, endWp.row, endWp.col, layout);
    ctx.imageSmoothingEnabled = prevSmooth;
  }

  // Start cell — rift strip (procedural frame) / static sprite / fallback green square
  const startSize = cellSize * startVisual.scale;
  const sx = startPt.x - startSize / 2;
  const sy = startPt.y - startSize / 2;
  if (pathStartStripImg && pathStartStripImg.complete) {
    const fh = pathStartStripImg.naturalHeight;
    const fw = fh;
    const n = Math.floor(pathStartStripImg.naturalWidth / fw);
    if (n >= 2) {
      const frame = Math.floor(animMs / RIFT_STRIP_MS_PER_FRAME) % n;
      ctx.drawImage(
        pathStartStripImg,
        frame * fw,
        0,
        fw,
        fh,
        sx,
        sy,
        startSize,
        startSize
      );
    } else if (pathStartFallbackImg && pathStartFallbackImg.complete) {
      ctx.drawImage(pathStartFallbackImg, sx, sy, startSize, startSize);
    }
  } else if (pathStartFallbackImg && pathStartFallbackImg.complete) {
    ctx.drawImage(pathStartFallbackImg, sx, sy, startSize, startSize);
  } else {
    ctx.fillStyle = "rgba(92, 26, 26, 0.2)";
    ctx.fillRect(startPt.x - cellSize / 2, startPt.y - cellSize / 2, cellSize, cellSize);
    const labelSize = Math.max(9, Math.floor(cellSize * 0.35));
    ctx.font = `bold ${labelSize}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = START_CLR;
    ctx.fillText("S", startPt.x, startPt.y);
  }

  // End cell — altar sprite or fallback blue square
  if (pathEndImg && pathEndImg.complete) {
    const endSize = cellSize * endVisual.scale;
    ctx.drawImage(
      pathEndImg,
      endPt.x - endSize / 2,
      endPt.y - endSize / 2,
      endSize,
      endSize
    );
  } else {
    ctx.fillStyle = "rgba(212, 175, 55, 0.2)";
    ctx.fillRect(endPt.x - cellSize / 2, endPt.y - cellSize / 2, cellSize, cellSize);
    const labelSize = Math.max(9, Math.floor(cellSize * 0.35));
    ctx.font = `bold ${labelSize}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = END_CLR;
    ctx.fillText("E", endPt.x, endPt.y);
  }

  // Waypath overlay removed: monsters walk BFS-shortest through the grid;
  // drawing a synthetic line between waypoints misleads players when towers
  // reroute the path. Start (rift) + end (altar) sprites above mark entry/exit.
  void pathPoints;
}

// --- Towers ---

function drawBattleBuildingBase(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  cellSize: number,
  battle: Required<BuildingBattleVisual>
): void {
  ctx.save();
  ctx.fillStyle = `rgba(0, 0, 0, ${battle.shadowAlpha})`;
  ctx.beginPath();
  ctx.ellipse(x, y + cellSize * 0.2, cellSize * 0.34 * battle.scaleX, cellSize * 0.13, 0, 0, Math.PI * 2);
  ctx.fill();
  if (battle.haloScale > 0) {
    const halo = ctx.createRadialGradient(x, y, 0, x, y, cellSize * battle.haloScale);
    halo.addColorStop(0, battle.haloColor);
    halo.addColorStop(1, "rgba(0, 0, 0, 0)");
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(x, y, cellSize * battle.haloScale, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function drawBattleBuildingSprite(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  cellSize: number,
  battle: Required<BuildingBattleVisual>,
  baseWidthCells: number,
  baseHeightCells: number
): void {
  const width = cellSize * baseWidthCells * battle.scaleX;
  const height = cellSize * baseHeightCells * battle.scaleY;
  ctx.drawImage(img, x - width / 2, y - height * battle.anchorY, width, height);
}

// --- Hover ghost ---

function drawHoverGhost(
  ctx: CanvasRenderingContext2D,
  state: GameStateView,
  layout: GridLayout
): void {
  if (!_hoverCell) return;
  if (state.scene !== "game") return;
  if (state.won !== null && state.won !== undefined) return;
  if (_dragState || state.dragState) return;
  const rows = state.field?.rows ?? DEFAULT_ROWS;
  const cols = state.field?.cols ?? DEFAULT_COLS;
  if (_hoverCell.row < 0 || _hoverCell.row >= rows) return;
  if (_hoverCell.col < 0 || _hoverCell.col >= cols) return;
  // Skip if cell already has a tower/wall.
  if (state.towers?.some(t => t.cell.row === _hoverCell!.row && t.cell.col === _hoverCell!.col)) return;
  // Skip if cell is spawn or altar.
  const waypoints = state.waypoints ?? [];
  if (waypoints.length >= 2) {
    const start = waypoints[0], end = waypoints[waypoints.length - 1];
    if ((start.row === _hoverCell.row && start.col === _hoverCell.col) ||
        (end.row === _hoverCell.row && end.col === _hoverCell.col)) return;
  }

  // Check if cell is a field obstacle — draw invalid (red) ghost instead of normal one.
  const isBlocked = state.blockedCells?.some(
    c => c.row === _hoverCell!.row && c.col === _hoverCell!.col
  ) ?? false;

  const { cellSize } = layout;
  const { x, y } = cellPos(_hoverCell.row, _hoverCell.col, layout);
  const half = cellSize * 0.4;
  const mode = state.placementMode ?? "Tower";

  if (isBlocked) {
    // Red "invalid" ghost: can't build on rock.
    ctx.save();
    ctx.globalAlpha = 0.55;
    ctx.fillStyle = "rgba(200, 40, 30, 0.7)";
    ctx.fillRect(x - half, y - half, half * 2, half * 2);
    ctx.strokeStyle = "rgba(255, 80, 60, 0.9)";
    ctx.lineWidth = 2;
    ctx.strokeRect(x - half, y - half, half * 2, half * 2);
    ctx.restore();
    return;
  }

  ctx.save();
  ctx.globalAlpha = 0.45;
  if (mode === "Wall") {
    ctx.fillStyle = "#6a5848";
    ctx.fillRect(x - half, y - half, half * 2, half * 2);
    ctx.strokeStyle = "#1a1612";
    ctx.lineWidth = 2;
    ctx.strokeRect(x - half, y - half, half * 2, half * 2);
  } else if (mode === "Amplifier") {
    ctx.fillStyle = "#2a1a4a";
    ctx.fillRect(x - half, y - half * 0.6, half * 2, half * 1.2);
    ctx.strokeStyle = "#d4af37";
    ctx.lineWidth = 2;
    ctx.strokeRect(x - half, y - half * 0.6, half * 2, half * 1.2);
    ctx.fillStyle = "#d4af37";
    ctx.beginPath();
    ctx.moveTo(x, y - half * 0.9);
    ctx.lineTo(x + half * 0.45, y - half * 0.2);
    ctx.lineTo(x, y + half * 0.3);
    ctx.lineTo(x - half * 0.45, y - half * 0.2);
    ctx.closePath();
    ctx.fill();
  } else if (mode === "Trap") {
    ctx.strokeStyle = "#556677";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(x, y, half * 0.9, 0, Math.PI * 2);
    ctx.stroke();
    // spikes
    for (let i = 0; i < 6; i++) {
      const a = (i * Math.PI * 2) / 6;
      ctx.beginPath();
      ctx.moveTo(x + Math.cos(a) * half * 0.9, y + Math.sin(a) * half * 0.9);
      ctx.lineTo(x + Math.cos(a) * half * 1.1, y + Math.sin(a) * half * 1.1);
      ctx.stroke();
    }
  } else if (mode === "Shrine") {
    // Marble pedestal ghost
    ctx.fillStyle = "#f0ece4";
    ctx.beginPath();
    ctx.arc(x, y, half * 0.9, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = VICTORY_CLR;
    ctx.lineWidth = 2;
    ctx.stroke();
    // Cross
    const cs = half * 0.35;
    ctx.strokeStyle = VICTORY_CLR;
    ctx.lineWidth = Math.max(2, half * 0.12);
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(x, y - cs);
    ctx.lineTo(x, y + cs);
    ctx.moveTo(x - cs * 0.65, y);
    ctx.lineTo(x + cs * 0.65, y);
    ctx.stroke();
  } else {
    // Tower / Chapel ghost
    const chapelImg = buildingImgs.get("Tower") ?? null;
    if (chapelImg && chapelImg.complete) {
      ctx.drawImage(chapelImg, x - half, y - half * 1.4, half * 2, half * 2.4);
    } else {
      ctx.fillStyle = SEPARATOR;
      ctx.fillRect(x - half, y - half, half * 2, half * 2);
    }
  }
  ctx.restore();

  // Highlight cell border
  ctx.save();
  ctx.strokeStyle = "rgba(212, 175, 55, 0.6)";
  ctx.lineWidth = 1.5;
  ctx.setLineDash([3, 2]);
  ctx.strokeRect(x - half - 2, y - half - 2, half * 2 + 4, half * 2 + 4);
  ctx.restore();
}

function drawChapelGlow(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  color: string
): void {
  ctx.save();
  // Chapel glow is centered a bit higher than the cell center (at the spire)
  const glowY = y - radius * 0.8;
  const glowR = radius * 3.5;
  const grad = ctx.createRadialGradient(x, glowY, radius * 0.2, x, glowY, glowR);
  grad.addColorStop(0, color + "33"); // Soft colored glow
  grad.addColorStop(0.5, color + "11");
  grad.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = grad;
  ctx.globalCompositeOperation = "screen";
  ctx.beginPath();
  ctx.arc(x, glowY, glowR, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawTowers(
  ctx: CanvasRenderingContext2D,
  towers: NonNullable<GameStateView["towers"]>,
  layout: GridLayout,
  visual: CampaignVisualConfig
): void {
  const { cellSize } = layout;
  const half = cellSize * 0.4;

  for (const t of towers) {
    if (!t.cell || typeof t.cell.row !== "number") continue;
    const { x, y } = cellPos(t.cell.row, t.cell.col, layout);
    const gem = t.gemSlots[0]?.gem ?? null;

    const buildingSprite = buildingImgs.get(t.type as BuildingType) ?? null;
    const battleVisual = buildingBattleVisual(visual, t.type as BuildingType);
    drawBattleBuildingBase(ctx, x, y, cellSize, battleVisual);

    // Wall: Sanctified-Lands stone wall sprite; procedural fallback.
    if (t.type === "Wall") {
      if (buildingSprite && buildingSprite.complete) {
        // Wall sprite has wide aspect (top-view) — fit inside cell with slight overhang up.
        drawBattleBuildingSprite(ctx, buildingSprite, x, y, cellSize, battleVisual, 0.8, 0.8);
      } else {
        // Procedural fallback: stone block
        ctx.fillStyle = "#6a5848";
        ctx.fillRect(x - half, y - half, half * 2, half * 2);
        ctx.strokeStyle = "#3a2820";
        ctx.lineWidth = 1;
        for (let i = 0; i < 4; i++) {
          const by = y - half + i * (half / 2);
          ctx.beginPath();
          ctx.moveTo(x - half, by);
          ctx.lineTo(x + half, by);
          ctx.stroke();
          const offset = i % 2 ? 0 : half / 2;
          ctx.beginPath();
          ctx.moveTo(x - half + offset, by);
          ctx.lineTo(x - half + offset, by + half / 2);
          ctx.stroke();
        }
        ctx.strokeStyle = "#1a1612";
        ctx.lineWidth = 2;
        ctx.strokeRect(x - half, y - half, half * 2, half * 2);
      }
      continue; // skip chapel sprite + gem overlay
    }

    // Amplifier: praying monk sprite — no gem slot, golden range-glow underneath.
    if (t.type === "Amplifier") {
      // Subtle radial glow to hint range (drawn first so monk sits on top).
      const glow = ctx.createRadialGradient(x, y - half * 0.3, 0, x, y - half * 0.3, cellSize * 2);
      glow.addColorStop(0, "rgba(212, 175, 55, 0.1)");
      glow.addColorStop(1, "rgba(212, 175, 55, 0)");
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(x, y - half * 0.3, cellSize * 2, 0, Math.PI * 2);
      ctx.fill();
      if (buildingSprite && buildingSprite.complete) {
        // Monk sits fully inside the cell, slight halo overhang.
        drawBattleBuildingSprite(ctx, buildingSprite, x, y, cellSize, battleVisual, 0.8, 0.88);
      } else {
        // Procedural fallback: dark pedestal + gold crystal.
        ctx.fillStyle = "#2a1a4a";
        ctx.fillRect(x - half, y - half * 0.6, half * 2, half * 1.2);
        ctx.strokeStyle = "#d4af37";
        ctx.lineWidth = 2;
        ctx.strokeRect(x - half, y - half * 0.6, half * 2, half * 1.2);
        const ampT = performance.now() / 400;
        const ampPulse = 1 + 0.1 * Math.sin(ampT);
        const crystalR = half * 0.45 * ampPulse;
        const crystalY = y - half * 0.9;
        ctx.fillStyle = "#d4af37";
        ctx.beginPath();
        ctx.moveTo(x, crystalY);
        ctx.lineTo(x + crystalR, y - half * 0.2);
        ctx.lineTo(x, y + half * 0.3);
        ctx.lineTo(x - crystalR, y - half * 0.2);
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = "#f0f4fa";
        ctx.lineWidth = 1;
        ctx.stroke();
      }
      continue; // no gem overlay for amplifier
    }

    // Trap: solar-cross sigil sprite; keep gem overlay for level/color.
    if (t.type === "Trap") {
      ctx.save();
      if (buildingSprite && buildingSprite.complete) {
        drawBattleBuildingSprite(ctx, buildingSprite, x, y, cellSize, battleVisual, 0.8, 0.8);
      } else {
        // Procedural fallback: dark disc with teeth.
        ctx.fillStyle = "#1a1a1a";
        ctx.beginPath();
        ctx.arc(x, y, half, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = "#3a3a4a";
        ctx.lineWidth = 2;
        ctx.stroke();
        const teethCount = 6;
        const toothOuter = half * 1.25;
        const toothInner = half * 0.85;
        const trapT = performance.now() / 900;
        ctx.fillStyle = "#2a2a3a";
        for (let i = 0; i < teethCount; i++) {
          const ang = (i / teethCount) * Math.PI * 2 + trapT * 0.3;
          const midAng = ang + Math.PI / teethCount;
          ctx.beginPath();
          ctx.moveTo(x + Math.cos(ang - 0.2) * toothInner, y + Math.sin(ang - 0.2) * toothInner);
          ctx.lineTo(x + Math.cos(midAng) * toothOuter, y + Math.sin(midAng) * toothOuter);
          ctx.lineTo(x + Math.cos(ang + 0.2) * toothInner, y + Math.sin(ang + 0.2) * toothInner);
          ctx.closePath();
          ctx.fill();
          ctx.strokeStyle = "#555580";
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      }
      // Inner glow if gem present
      if (gem) {
        const gemColor = getGemColor(gem.type, gem.secondaryType);
        const pulseMult = 1 + 0.15 * Math.sin(performance.now() / 250);
        const innerR = half * 0.42 * pulseMult;
        const glow = ctx.createRadialGradient(x, y, 0, x, y, half * 0.85);
        glow.addColorStop(0, gemColor + "cc");
        glow.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(x, y, half * 0.85, 0, Math.PI * 2);
        ctx.fill();
        // Relic icon in center
        drawRelicIcon(ctx, gem.type, gem.level, x, y, innerR, gemColor, gem.secondaryType);
      }
      ctx.restore();
      continue; // skip regular gem overlay
    }

    // Shrine: reliquary altar sprite + charge ring; procedural fallback.
    if (t.type === "Shrine") {
      ctx.save();
      const pedR = half * 0.9;
      if (buildingSprite && buildingSprite.complete) {
        drawBattleBuildingSprite(ctx, buildingSprite, x, y, cellSize, battleVisual, 0.8, 0.88);
      } else {
        // Procedural fallback: marble base + pulsing cross.
        ctx.fillStyle = "#f0ece4";
        ctx.beginPath();
        ctx.arc(x, y, pedR, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = VICTORY_CLR;
        ctx.lineWidth = 2;
        ctx.stroke();
        const shrineT = performance.now() / 600;
        const crossSize = half * 0.35 * (0.85 + 0.15 * Math.sin(shrineT));
        ctx.strokeStyle = VICTORY_CLR;
        ctx.lineWidth = Math.max(2, half * 0.12);
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(x, y - crossSize);
        ctx.lineTo(x, y + crossSize);
        ctx.moveTo(x - crossSize * 0.65, y);
        ctx.lineTo(x + crossSize * 0.65, y);
        ctx.stroke();
      }
      // Charge progress ring
      const viewLastDischarge = (t as unknown as { lastDischargeAt?: number }).lastDischargeAt ?? 0;
      const SHRINE_CHARGE_MS_RENDER = 15000;
      const chargeProgress = Math.min(1, (drawTowersStateElapsedMs - viewLastDischarge) / SHRINE_CHARGE_MS_RENDER);
      const ringR = pedR + half * 0.18;
      // Background ring
      ctx.strokeStyle = "rgba(212, 175, 55, 0.2)";
      ctx.lineWidth = Math.max(2, half * 0.18);
      ctx.lineCap = "butt";
      ctx.beginPath();
      ctx.arc(x, y, ringR, 0, Math.PI * 2);
      ctx.stroke();
      // Filled arc
      if (chargeProgress > 0) {
        ctx.strokeStyle = chargeProgress >= 1 ? "#ffffff" : VICTORY_CLR;
        ctx.lineWidth = Math.max(2, half * 0.18);
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.arc(x, y, ringR, -Math.PI / 2, -Math.PI / 2 + chargeProgress * Math.PI * 2);
        ctx.stroke();
      }
      ctx.restore();
      continue; // skip regular gem overlay
    }

    // Base building sprite. Falls back to a plain square when the manifest image is unavailable.
    const towerSprite = (buildingSprite && buildingSprite.complete) ? buildingSprite : null;
    if (towerSprite) {
      // Allow spire to extend ~30% above the cell top
      drawBattleBuildingSprite(ctx, towerSprite, x, y, cellSize, battleVisual, 0.8, 0.96);
    } else {
      ctx.fillStyle = SEPARATOR;
      ctx.fillRect(x - half, y - half, half * 2, half * 2);
      ctx.strokeStyle = "rgba(255,255,255,0.2)";
      ctx.lineWidth = 1;
      ctx.strokeRect(x - half, y - half, half * 2, half * 2);
    }

    // Overlay: gem disc at the chapel's spire socket (upper portion of the sprite).
    // The chapel art places the cross/socket roughly 40% above the cell center.
    if (gem) {
      const gemY = y - half * 0.35 * battleVisual.scaleY;
      const gemColor = getGemColor(gem.type, gem.secondaryType);

      // B13: Chapel Glow (colored aura around the building)
      drawChapelGlow(ctx, x, y, half, gemColor);

      // Level-up visual: higher-grade relics are bigger, glow harder, pulse.
      const lvl = Math.max(1, gem.level);
      const sizeMult = 1 + Math.min(0.6, (lvl - 1) * 0.1); // cap +60% at lvl 7
      const pulse = lvl >= 5
        ? 1 + 0.08 * Math.sin(performance.now() / 180)
        : 1;
      const gemRadius = half * 0.38 * sizeMult * pulse;
      const haloScale = 2.2 + Math.min(1.3, (lvl - 1) * 0.18);
      // Glow halo
      const halo = ctx.createRadialGradient(x, gemY, 0, x, gemY, gemRadius * haloScale);
      halo.addColorStop(0, gemColor);
      halo.addColorStop(0.4, gemColor + "88");
      halo.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = halo;
      ctx.beginPath();
      ctx.arc(x, gemY, gemRadius * haloScale, 0, Math.PI * 2);
      ctx.fill();
      // Relic body (thematic icons)
      drawRelicIcon(ctx, gem.type, gem.level, x, gemY, gemRadius, gemColor, gem.secondaryType);

      // Star/sparkle for level 7+ (max is 9).
      if (lvl >= 7) {
        ctx.save();
        ctx.strokeStyle = "rgba(255,255,200,0.9)";
        ctx.lineWidth = 1;
        const r1 = gemRadius * 1.35;
        const r2 = gemRadius * 1.05;
        const tw = performance.now() / 320;
        for (let i = 0; i < 4; i++) {
          const a = tw + (i * Math.PI) / 2;
          ctx.beginPath();
          ctx.moveTo(x + Math.cos(a) * r1, gemY + Math.sin(a) * r1);
          ctx.lineTo(x + Math.cos(a) * r2, gemY + Math.sin(a) * r2);
          ctx.stroke();
        }
        ctx.restore();
      }
      // drawRelicIcon already called above
    }
  }
}

// --- Monsters ---

const WALK_FRAME_MS = 180;
const WALK_FRAMES = 4;

function drawMonsterStatusEffects(
  ctx: CanvasRenderingContext2D,
  monster: NonNullable<GameStateView["monsters"]>[number],
  x: number,
  y: number,
  spriteTopY: number,
  spriteSize: number,
  cellSize: number
): void {
  const statuses: Array<{ color: string; label: string }> = [];
  if (monster.poisoned) statuses.push({ color: "#45d66f", label: "P" });
  if (monster.slowed) statuses.push({ color: "#aa72ff", label: "S" });
  if (monster.shocked) statuses.push({ color: "#5ee8ff", label: "!" });
  if ((monster.armor ?? 0) > 0) statuses.push({ color: "#7fb4ff", label: String(Math.ceil(monster.armor ?? 0)) });
  if (statuses.length === 0) return;

  const now = performance.now();
  ctx.save();

  if (monster.slowed) {
    ctx.strokeStyle = "rgba(170, 114, 255, 0.75)";
    ctx.lineWidth = Math.max(1, cellSize * 0.035);
    ctx.beginPath();
    ctx.ellipse(x, y + cellSize * 0.12, spriteSize * 0.38, cellSize * 0.12, 0, 0, Math.PI * 2);
    ctx.stroke();
  }

  if (monster.poisoned) {
    const pulse = 0.45 + 0.18 * Math.sin(now * 0.009 + monster.id.length);
    ctx.fillStyle = `rgba(69, 214, 111, ${pulse})`;
    for (let i = 0; i < 3; i++) {
      const angle = now * 0.002 + i * 2.1 + monster.id.length;
      ctx.beginPath();
      ctx.arc(
        x + Math.cos(angle) * spriteSize * 0.28,
        y - cellSize * 0.18 + Math.sin(angle) * spriteSize * 0.18,
        Math.max(1.5, cellSize * 0.045),
        0,
        Math.PI * 2
      );
      ctx.fill();
    }
  }

  if (monster.shocked) {
    ctx.strokeStyle = "rgba(94, 232, 255, 0.9)";
    ctx.lineWidth = Math.max(1, cellSize * 0.025);
    const jitter = Math.sin(now * 0.04 + monster.id.length) * cellSize * 0.03;
    ctx.beginPath();
    ctx.moveTo(x - spriteSize * 0.25, y - spriteSize * 0.2);
    ctx.lineTo(x + jitter, y - spriteSize * 0.36);
    ctx.lineTo(x + spriteSize * 0.22, y - spriteSize * 0.05);
    ctx.stroke();
  }

  const badgeR = Math.max(5, cellSize * 0.105);
  const startX = x - ((statuses.length - 1) * badgeR * 1.45) / 2;
  const badgeY = spriteTopY - badgeR * 1.65;
  ctx.font = `bold ${Math.max(8, Math.floor(badgeR * 1.2))}px sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  for (let i = 0; i < statuses.length; i++) {
    const status = statuses[i];
    const bx = startX + i * badgeR * 1.45;
    ctx.fillStyle = "rgba(8, 7, 6, 0.82)";
    ctx.beginPath();
    ctx.arc(bx, badgeY, badgeR, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = status.color;
    ctx.lineWidth = Math.max(1, cellSize * 0.025);
    ctx.stroke();
    ctx.fillStyle = status.color;
    ctx.fillText(status.label, bx, badgeY + 0.5);
  }

  ctx.restore();
}

function drawMonsters(
  ctx: CanvasRenderingContext2D,
  monsters: NonNullable<GameStateView["monsters"]>,
  layout: GridLayout,
  visual: CampaignVisualConfig
): void {
  const { cellSize } = layout;
  const hpBarW = cellSize * 0.7;
  const hpBarH = 3;
  const animT = performance.now();
  const globalFrame = Math.floor(animT / WALK_FRAME_MS) % WALK_FRAMES;

  for (const m of monsters) {
    const { x, y } = cellPos(m.row, m.col, layout);
    // Phase-shift each monster so the pack doesn't lock-step.
    const charCode = m.id.charCodeAt(m.id.length - 1) | 0;
    const frame = (globalFrame + charCode) % WALK_FRAMES;

    const monsterType = m.type as MonsterType;
    const monsterVisual = getMonsterVisual(monsterType, visual);
    const scale = monsterVisual.scale;
    const spriteSize = cellSize * 1.1 * scale;

    const sheet = monsterSheetImgs.get(monsterType) ?? null;
    if (sheet && sheet.complete) {
      const sheetFrames = monsterVisual.sheetFrames;
      const sheetFrame = (Math.floor(animT / WALK_FRAME_MS) + charCode) % sheetFrames;
      const srcSize = monsterVisual.sheetFrameSize;
      const sheetRows = Math.max(1, Math.floor(sheet.naturalHeight / srcSize));
      const dirIdx = monsterSheetDirectionRow(m.dirIndex ?? 0, sheetRows);

      // Sprite top Y — monster hangs slightly below-center for grounded feel
      const spriteTopY = y - spriteSize * 0.55;

      // Isometric shadow
      ctx.save();
      ctx.globalAlpha = 0.45;
      ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
      ctx.beginPath();
      ctx.ellipse(x, y + cellSize * 0.08, spriteSize * 0.38, cellSize * 0.1, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      const prevSmoothing = ctx.imageSmoothingEnabled;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(
        sheet,
        sheetFrame * srcSize, dirIdx * srcSize, srcSize, srcSize,
        x - spriteSize / 2, spriteTopY, spriteSize, spriteSize
      );

      ctx.imageSmoothingEnabled = prevSmoothing;

      const tintColor = monsterVisual.tint ?? null;

      if (tintColor) {
        const auraR = spriteSize * 0.65;
        const aura = ctx.createRadialGradient(x, y, 0, x, y, auraR);
        aura.addColorStop(0, tintColor);
        aura.addColorStop(1, "rgba(0, 0, 0, 0)");
        ctx.fillStyle = aura;
        ctx.beginPath();
        ctx.arc(x, y, auraR, 0, Math.PI * 2);
        ctx.fill();
      }

      drawMonsterStatusEffects(ctx, m, x, y, spriteTopY, spriteSize, cellSize);

      // HP bar
      const barX = x - hpBarW / 2;
      const barY = spriteTopY - hpBarH - 2;
      ctx.fillStyle = HUD_BG;
      ctx.fillRect(barX, barY, hpBarW, hpBarH);
      const ratio = m.maxHp > 0 ? Math.max(0, m.hp / m.maxHp) : 0;
      ctx.fillStyle = ratio > 0.5 ? "#44cc44" : ratio > 0.25 ? "#cccc44" : "#cc4444";
      ctx.fillRect(barX, barY, hpBarW * ratio, hpBarH);
      continue;
    }

    // Per-type sprite selection; manifest fallback first, then neutral procedural shape.
    const manifestFallback = monsterFallbackImgs.get(monsterType) ?? null;
    const normalFallback = monsterFallbackImgs.get("Normal") ?? null;
    let walkSprite: HTMLImageElement | null = manifestFallback && manifestFallback.complete ? manifestFallback : null;
    if (!walkSprite && monsterType === "Giant" && normalFallback && normalFallback.complete) {
      walkSprite = normalFallback;
    }

    const tintColor = monsterVisual.tint ?? null;

    const useSprite = walkSprite !== null;
    const frameW = useSprite ? walkSprite!.naturalWidth / WALK_FRAMES : 0;
    const frameH = useSprite ? walkSprite!.naturalHeight : 0;

    // Sprite top Y — monster hangs slightly below-center for grounded feel
    const spriteTopY = y - spriteSize * 0.55;

    // Isometric shadow (drawn before sprite)
    ctx.save();
    ctx.globalAlpha = 0.45;
    ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
    ctx.beginPath();
    ctx.ellipse(x, y + cellSize * 0.08, spriteSize * 0.38, cellSize * 0.1, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    if (useSprite) {
      ctx.drawImage(
        walkSprite!,
        frame * frameW, 0, frameW, frameH,
        x - spriteSize / 2, spriteTopY, spriteSize, spriteSize,
      );
      if (tintColor) {
        // Soft radial aura hint for Giant (fire) / Runner (spectre).
        const auraR = spriteSize * 0.65;
        const aura = ctx.createRadialGradient(x, y, 0, x, y, auraR);
        aura.addColorStop(0, tintColor);
        aura.addColorStop(1, "rgba(0, 0, 0, 0)");
        ctx.fillStyle = aura;
        ctx.beginPath();
        ctx.arc(x, y, auraR, 0, Math.PI * 2);
        ctx.fill();
      }
    } else {
      const radius = cellSize * 0.3 * scale;
      ctx.fillStyle = DEFEAT_CLR;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = START_CLR;
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    // HP bar — above the sprite top
    drawMonsterStatusEffects(ctx, m, x, y, spriteTopY, spriteSize, cellSize);

    const barX = x - hpBarW / 2;
    const barY = spriteTopY - hpBarH - 2;
    ctx.fillStyle = HUD_BG;
    ctx.fillRect(barX, barY, hpBarW, hpBarH);

    const ratio = m.maxHp > 0 ? Math.max(0, m.hp / m.maxHp) : 0;
    ctx.fillStyle =
      ratio > 0.5 ? "#44cc44" : ratio > 0.25 ? "#cccc44" : "#cc4444";
    ctx.fillRect(barX, barY, hpBarW * ratio, hpBarH);
  }
}

function monsterSheetDirectionRow(dirIndex: number, sheetRows: number): number {
  if (sheetRows >= 8) return ((dirIndex % 8) + 8) % 8;
  if (sheetRows === 4) {
    // Compact cardinal sheet order: S, E, N, W. Current pathfinding is 4-connected,
    // but diagonal indices can still be rounded to the nearest cardinal for safety.
    const cardinal = [0, 1, 1, 2, 2, 3, 3, 0];
    return cardinal[((dirIndex % 8) + 8) % 8];
  }
  return 0;
}

// --- Shots (projectiles in flight) — B5 per-type styles ---

function drawShots(
  ctx: CanvasRenderingContext2D,
  shots: NonNullable<GameStateView["shots"]>,
  layout: GridLayout
): void {
  const { cellSize } = layout;
  const now = performance.now();
  const baseRadius = Math.max(2, cellSize * 0.12);

  for (const s of shots) {
    const { x, y } = cellPos(s.row, s.col, layout);
    ctx.save();
    if (activeCampaign.ui?.theme === "modern") {
      ctx.shadowColor = getGemColor(s.gemType, s.secondaryGemType);
      ctx.shadowBlur = 12;
    }

    // Mixed color for dual-type shots (B13)
    const color = getGemColor(s.gemType, s.secondaryGemType);

    if (s.secondaryGemType) {
      // For dual shots, we use the mixed color and a simpler circle/pulsing style for now
      // as the per-type styles are complex.
      ctx.fillStyle = color;
      const pulse = 1 + 0.2 * Math.sin(now * 0.01 + s.id.length);
      ctx.beginPath();
      ctx.arc(x, y, baseRadius * pulse, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = "rgba(255,255,255,0.4)";
      ctx.lineWidth = 1;
      ctx.stroke();
    } else {
      switch (s.gemType) {
        case "Yellow": _drawShotYellow(ctx, x, y, baseRadius, s.id); break;
        case "Orange": _drawShotOrange(ctx, x, y, baseRadius); break;
        case "Red":    _drawShotRed(ctx, x, y, baseRadius, now); break;
        case "Purple": _drawShotPurple(ctx, x, y, baseRadius); break;
        case "Green":  _drawShotGreen(ctx, x, y, baseRadius, s.id); break;
        case "Blue":   _drawShotBlue(ctx, x, y, baseRadius, s.id); break;
        case "Cyan":   _drawShotCyan(ctx, x, y, cellSize, s.id, now); break;
        case "Lime":   _drawShotLime(ctx, x, y, baseRadius, now); break;
        default: {
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.arc(x, y, baseRadius, 0, Math.PI * 2);
          ctx.fill();
          break;
        }
      }
    }
    ctx.restore();
  }
}

/** Yellow (MANA_GAIN): gold spark + trail dots */
function _drawShotYellow(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, id: string): void {
  const trailAngle = (id.charCodeAt(0) * 0.37) % (Math.PI * 2);
  for (let i = 1; i <= 3; i++) {
    const tx = x - Math.cos(trailAngle) * r * i * 1.8;
    const ty = y - Math.sin(trailAngle) * r * i * 1.8;
    ctx.globalAlpha = 0.5 / i;
    ctx.fillStyle = "#ffdd00";
    ctx.beginPath();
    ctx.arc(tx, ty, r * (0.7 / i), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
  const grad = ctx.createRadialGradient(x, y, 0, x, y, r * 1.4);
  grad.addColorStop(0, "#ffffc0");
  grad.addColorStop(0.5, "#ffcc00");
  grad.addColorStop(1, "rgba(255,180,0,0)");
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(x, y, r * 1.4, 0, Math.PI * 2);
  ctx.fill();
}

/** Orange (SPLASH): fireball with radial gradient + faint smoke */
function _drawShotOrange(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  const smoke = ctx.createRadialGradient(x, y, r, x, y, r * 2.5);
  smoke.addColorStop(0, "rgba(80,40,10,0.3)");
  smoke.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = smoke;
  ctx.beginPath();
  ctx.arc(x, y, r * 2.5, 0, Math.PI * 2);
  ctx.fill();
  const fire = ctx.createRadialGradient(x - r * 0.3, y - r * 0.3, 0, x, y, r * 1.5);
  fire.addColorStop(0, "#fff0a0");
  fire.addColorStop(0.4, "#ff8800");
  fire.addColorStop(1, "rgba(180,40,0,0)");
  ctx.fillStyle = fire;
  ctx.beginPath();
  ctx.arc(x, y, r * 1.5, 0, Math.PI * 2);
  ctx.fill();
}

/** Red (CRIT): pulsing crimson sphere */
function _drawShotRed(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, now: number): void {
  const pulse = 1 + 0.25 * Math.sin(now * 0.012);
  const pr = r * 1.3 * pulse;
  const grad = ctx.createRadialGradient(x, y, 0, x, y, pr);
  grad.addColorStop(0, "#ffaaaa");
  grad.addColorStop(0.4, "#ff2200");
  grad.addColorStop(1, "rgba(120,0,0,0)");
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(x, y, pr, 0, Math.PI * 2);
  ctx.fill();
}

/** Purple (SLOW): misty fog blob */
function _drawShotPurple(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  const grad = ctx.createRadialGradient(x, y, 0, x, y, r * 2.2);
  grad.addColorStop(0, "rgba(200,100,255,0.7)");
  grad.addColorStop(0.5, "rgba(140,0,200,0.4)");
  grad.addColorStop(1, "rgba(80,0,120,0)");
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(x, y, r * 2.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#cc44ff";
  ctx.beginPath();
  ctx.arc(x, y, r * 0.7, 0, Math.PI * 2);
  ctx.fill();
}

/** Green (POISON): drop + trail */
function _drawShotGreen(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, id: string): void {
  const angle = (id.charCodeAt(0) * 0.41) % (Math.PI * 2);
  for (let i = 1; i <= 3; i++) {
    const tx = x - Math.cos(angle) * r * i * 1.5;
    const ty = y - Math.sin(angle) * r * i * 1.5;
    ctx.globalAlpha = 0.4 / i;
    ctx.fillStyle = "#00dd44";
    ctx.beginPath();
    ctx.arc(tx, ty, r * (0.6 / i), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
  ctx.fillStyle = "#00ff44";
  ctx.beginPath();
  ctx.arc(x, y, r * 1.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#004420";
  ctx.lineWidth = 1;
  ctx.stroke();
}

/** Blue (ARMOR_PIERCE): white dart + shadow trail */
function _drawShotBlue(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, id: string): void {
  const angle = (id.charCodeAt(0) * 0.31) % (Math.PI * 2);
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const len = r * 4;
  ctx.strokeStyle = "rgba(80,120,255,0.4)";
  ctx.lineWidth = r * 1.2;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(x + cos * r, y + sin * r);
  ctx.lineTo(x - cos * len, y - sin * len);
  ctx.stroke();
  ctx.strokeStyle = "#ddeeff";
  ctx.lineWidth = r * 0.8;
  ctx.beginPath();
  ctx.moveTo(x + cos * r * 0.5, y + sin * r * 0.5);
  ctx.lineTo(x - cos * len * 0.7, y - sin * len * 0.7);
  ctx.stroke();
}

/** Cyan (SHOCK): 3-arm zigzag lightning bolt */
function _drawShotCyan(ctx: CanvasRenderingContext2D, x: number, y: number, cellSize: number, id: string, now: number): void {
  const seed = id.charCodeAt(0) + (id.charCodeAt(id.length - 1) | 0);
  const spread = cellSize * 0.18;
  const r = Math.max(2, cellSize * 0.12);
  for (let arm = 0; arm < 3; arm++) {
    const baseAngle = (arm / 3) * Math.PI * 2 + seed * 0.07;
    const len = cellSize * 0.35;
    const ex = x + Math.cos(baseAngle) * len;
    const ey = y + Math.sin(baseAngle) * len;
    const perp = baseAngle + Math.PI / 2;
    const midX = (x + ex) / 2 + Math.cos(perp) * spread * ((seed % 3) * 0.4 - 0.3);
    const midY = (y + ey) / 2 + Math.sin(perp) * spread * ((seed % 3) * 0.4 - 0.3);
    ctx.strokeStyle = "rgba(150,220,255,0.35)";
    ctx.lineWidth = 4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(midX, midY);
    ctx.lineTo(ex, ey);
    ctx.stroke();
    ctx.strokeStyle = "#ccffff";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(midX, midY);
    ctx.lineTo(ex, ey);
    ctx.stroke();
  }
  ctx.globalAlpha = 0.6 + 0.4 * Math.abs(Math.sin(now * 0.02));
  ctx.fillStyle = "#ffffff";
  ctx.beginPath();
  ctx.arc(x, y, r * 1.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 1;
}

/** Lime (CHAIN_HIT): pulsing green-yellow ring */
function _drawShotLime(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, now: number): void {
  const pulse = 1 + 0.2 * Math.sin(now * 0.015);
  const pr = r * 1.4 * pulse;
  const grad = ctx.createRadialGradient(x, y, 0, x, y, pr);
  grad.addColorStop(0, "#eeffaa");
  grad.addColorStop(0.5, "#88ff00");
  grad.addColorStop(1, "rgba(60,160,0,0)");
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(x, y, pr, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = `rgba(100,255,50,${0.4 * pulse})`;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(x, y, pr * 1.6, 0, Math.PI * 2);
  ctx.stroke();
}

// === B5: Visual effects — death smoke + respawn halos ===

function _updateVisualEffects(state: GameStateView): void {
  const now = performance.now();
  const currentMonsters = state.monsters ?? [];
  const currentIds = new Set(currentMonsters.map(m => m.id));

  for (const [id, prev] of _prevMonsters) {
    if (!currentIds.has(id)) {
      _deathEffects.push({ row: prev.row, col: prev.col, startMs: now });
    }
  }

  for (const m of currentMonsters) {
    const prev = _prevMonsters.get(m.id);
    if (prev) {
      const dr = m.row - prev.row;
      const dc = m.col - prev.col;
      if (Math.sqrt(dr * dr + dc * dc) > BANISH_JUMP_THRESHOLD) {
        _respawnEffects.set(m.id, now);
      }
    }
  }

  _prevMonsters.clear();
  for (const m of currentMonsters) {
    _prevMonsters.set(m.id, { row: m.row, col: m.col });
  }

  for (let i = _deathEffects.length - 1; i >= 0; i--) {
    if (now - _deathEffects[i].startMs > DEATH_DURATION_MS) _deathEffects.splice(i, 1);
  }
  for (const [id, startMs] of _respawnEffects) {
    if (now - startMs > RESPAWN_DURATION_MS) _respawnEffects.delete(id);
  }
}

function drawDeathEffects(ctx: CanvasRenderingContext2D, layout: GridLayout): void {
  const now = performance.now();
  const { cellSize } = layout;
  for (const de of _deathEffects) {
    const t = Math.max(0, Math.min(1, (now - de.startMs) / DEATH_DURATION_MS));
    const { x, y } = cellPos(de.row, de.col, layout);
    ctx.save();
    if (t < 0.5) {
      const phase = t / 0.5;
      const radius = cellSize * 0.15 + cellSize * 0.45 * phase;
      const alpha = 0.8 * (1 - phase * 0.75);
      const grad = ctx.createRadialGradient(x, y, 0, x, y, radius);
      grad.addColorStop(0, `rgba(20,10,10,${alpha})`);
      grad.addColorStop(0.5, `rgba(40,20,10,${alpha * 0.7})`);
      grad.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    }
    for (let i = 0; i < 4; i++) {
      const angle = (i / 4) * Math.PI * 2 + (de.row * 7 + de.col * 13) * 0.1;
      const speed = cellSize * 0.55 * t;
      const px = x + Math.cos(angle) * speed;
      const py = y + Math.sin(angle) * speed;
      ctx.globalAlpha = Math.max(0, 1 - t * 1.5);
      ctx.fillStyle = t < 0.4 ? "#ff6600" : "#661100";
      ctx.beginPath();
      ctx.arc(px, py, Math.max(0.5, cellSize * 0.06 * (1 - t)), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
}

function drawRespawnHalos(
  ctx: CanvasRenderingContext2D,
  monsters: NonNullable<GameStateView["monsters"]>,
  layout: GridLayout
): void {
  if (_respawnEffects.size === 0) return;
  const now = performance.now();
  const { cellSize } = layout;
  const haloR = cellSize * 0.55;
  for (const m of monsters) {
    const startMs = _respawnEffects.get(m.id);
    if (startMs === undefined) continue;
    const t = Math.max(0, Math.min(1, (now - startMs) / RESPAWN_DURATION_MS));
    const pulse = Math.abs(Math.sin((now - startMs) * 0.007));
    const alpha = (1 - t) * 0.65 * (0.4 + 0.6 * pulse);
    const { x, y } = cellPos(m.row, m.col, layout);
    const grad = ctx.createRadialGradient(x, y, haloR * 0.5, x, y, haloR * 1.3);
    grad.addColorStop(0, "rgba(255,0,0,0)");
    grad.addColorStop(0.6, `rgba(220,30,10,${alpha})`);
    grad.addColorStop(1, "rgba(180,0,0,0)");
    ctx.save();
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x, y, haloR * 1.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

function drawTowerStats(
  ctx: CanvasRenderingContext2D,
  state: GameStateView,
  layout: GridLayout
): void {
  if (!_hoverCell) return;
  const tower = state.towers?.find(t => t.cell.row === _hoverCell!.row && t.cell.col === _hoverCell!.col);
  if (!tower || !tower.stats) return;

  const { cellSize } = layout;
  const { x, y } = cellPos(tower.cell.row, tower.cell.col, layout);

  ctx.save();
  ctx.strokeStyle = "rgba(255, 255, 255, 0.5)";
  ctx.lineWidth = 1;
  ctx.setLineDash([4, 4]);
  // Circle radius covering the cells
  const r = (tower.stats.range + 0.5) * cellSize;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();

  const pad = 6;
  const lineH = 16;
  const lines = [
    t("hud.damage", { min: tower.stats.damageMin, max: tower.stats.damageMax }),
    t("hud.range", { range: tower.stats.range.toFixed(1) }),
    t("hud.fireRate", { rate: (1000 / tower.stats.fireRateMs).toFixed(2) })
  ];
  if (tower.stats.upgradeCost !== undefined) {
    lines.push(t("hud.upgradeCost", { cost: tower.stats.upgradeCost }));
  }
  if (tower.stats.specialEffect) {
    lines.push(tower.stats.specialEffect);
  }
  for (const detail of tower.stats.effectDetails ?? []) {
    lines.push(detail);
  }

  ctx.save();
  ctx.font = "12px sans-serif";
  let maxW = 0;
  for (const l of lines) {
    const w = ctx.measureText(l).width;
    if (w > maxW) maxW = w;
  }

  const boxW = maxW + pad * 2;
  const boxH = lines.length * lineH + pad * 2;
  const tx = x + cellSize * 0.7;
  const ty = y - boxH / 2;

  ctx.fillStyle = "rgba(10, 8, 6, 0.9)";
  ctx.strokeStyle = "#d4af37";
  ctx.lineWidth = 1;
  ctx.fillRect(tx, ty, boxW, boxH);
  ctx.strokeRect(tx, ty, boxW, boxH);

  ctx.fillStyle = "#e8dcb4";
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i], tx + pad, ty + pad + i * lineH);
  }

  ctx.restore();
}

// --- Hit flashes (fade-out squares) ---


function drawHitFlashes(
  ctx: CanvasRenderingContext2D,
  flashes: NonNullable<GameStateView["hitFlashes"]>,
  layout: GridLayout
): void {
  const { cellSize } = layout;
  for (const f of flashes) {
    const progress = Math.max(0, Math.min(1, f.ageMs / f.durationMs));
    const alpha = 1 - progress;
    if (alpha <= 0) continue;
    const { x, y } = cellPos(f.row, f.col, layout);
    const color = getGemColor(f.gemType);
    ctx.save();
    ctx.globalAlpha = alpha;
    drawGemHitFlash(ctx, f.gemType, color, x, y, cellSize, progress);
    ctx.restore();
  }
}

function drawGemHitFlash(
  ctx: CanvasRenderingContext2D,
  gemType: string,
  color: string,
  x: number,
  y: number,
  cellSize: number,
  progress: number
): void {
  const radius = cellSize * (0.18 + progress * 0.32);
  const pulse = 1 - progress;

  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  const rgb = hexToRgb(color);
  const glow = ctx.createRadialGradient(x, y, 0, x, y, cellSize * 0.46);
  glow.addColorStop(0, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.67)`);
  glow.addColorStop(0.55, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.27)`);
  glow.addColorStop(1, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`);
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(x, y, cellSize * 0.46, 0, Math.PI * 2);
  ctx.fill();

  switch (gemType) {
    case "Green":
      ctx.fillStyle = "#45d66f";
      for (let i = 0; i < 5; i++) {
        const angle = i * 1.256 + progress * 2.2;
        const r = cellSize * (0.08 + i * 0.025 + progress * 0.16);
        ctx.beginPath();
        ctx.arc(
          x + Math.cos(angle) * r,
          y + Math.sin(angle) * r - cellSize * 0.08,
          Math.max(2, cellSize * 0.035 * pulse),
          0,
          Math.PI * 2
        );
        ctx.fill();
      }
      break;
    case "Purple":
      ctx.strokeStyle = "#aa72ff";
      ctx.lineWidth = Math.max(2, cellSize * 0.035);
      ctx.beginPath();
      ctx.ellipse(x, y + cellSize * 0.06, radius * 1.15, radius * 0.45, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.ellipse(x, y + cellSize * 0.06, radius * 0.7, radius * 0.25, 0, 0, Math.PI * 2);
      ctx.stroke();
      break;
    case "Cyan":
      ctx.strokeStyle = "#5ee8ff";
      ctx.lineWidth = Math.max(2, cellSize * 0.035);
      for (let i = 0; i < 3; i++) {
        const angle = i * (Math.PI * 2 / 3) - Math.PI / 2;
        const midX = x + Math.cos(angle) * radius * 0.45;
        const midY = y + Math.sin(angle) * radius * 0.45;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(midX + Math.sin(angle) * cellSize * 0.06, midY);
        ctx.lineTo(x + Math.cos(angle) * radius, y + Math.sin(angle) * radius);
        ctx.stroke();
      }
      break;
    case "Blue":
      ctx.strokeStyle = "#e9f2ff";
      ctx.lineWidth = Math.max(2, cellSize * 0.03);
      for (let i = -1; i <= 1; i++) {
        const off = i * cellSize * 0.11;
        ctx.beginPath();
        ctx.moveTo(x - radius * 0.75 + off, y - radius * 0.35);
        ctx.lineTo(x - radius * 0.2 + off, y + radius * 0.08);
        ctx.lineTo(x - radius * 0.45 + off, y + radius * 0.42);
        ctx.stroke();
      }
      break;
    case "Lime":
      ctx.strokeStyle = "#a8e030";
      ctx.lineWidth = Math.max(2, cellSize * 0.032);
      for (let i = 0; i < 3; i++) {
        ctx.beginPath();
        ctx.arc(x + (i - 1) * cellSize * 0.12, y, radius * 0.42, 0, Math.PI * 2);
        ctx.stroke();
      }
      break;
    default:
      ctx.strokeStyle = color;
      ctx.lineWidth = Math.max(2, cellSize * 0.04);
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x - radius * 0.7, y);
      ctx.lineTo(x + radius * 0.7, y);
      ctx.moveTo(x, y - radius * 0.7);
      ctx.lineTo(x, y + radius * 0.7);
      ctx.stroke();
      break;
  }
}

// --- Cell-flash overlay (B20: placement error feedback) ---

function drawCellFlashes(ctx: CanvasRenderingContext2D, layout: GridLayout): void {
  if (_cellFlashes.length === 0) return;
  const now = performance.now();
  // Expire and draw in one pass
  for (let i = _cellFlashes.length - 1; i >= 0; i--) {
    const f = _cellFlashes[i];
    const age = now - f.startMs;
    if (age >= f.durationMs) { _cellFlashes.splice(i, 1); continue; }
    const alpha = 1 - age / f.durationMs;
    const { x, y } = cellPos(f.row, f.col, layout);
    const { cellSize } = layout;
    ctx.save();
    ctx.globalAlpha = Math.max(0, alpha);
    ctx.fillStyle = f.color;
    ctx.fillRect(x - cellSize / 2, y - cellSize / 2, cellSize, cellSize);
    ctx.restore();
  }
}

// --- Ghost gem (DnD) ---

function drawGhostGem(
  ctx: CanvasRenderingContext2D,
  dragState: { gem: { type: string; level: number; secondaryType?: string }; screenX: number; screenY: number }
): void {
  const half = 15;
  ctx.globalAlpha = 0.8;
  const color = getGemColor(dragState.gem.type, dragState.gem.secondaryType);
  drawRelicIcon(
    ctx,
    dragState.gem.type,
    dragState.gem.level,
    dragState.screenX,
    dragState.screenY,
    half * 0.8,
    color,
    dragState.gem.secondaryType
  );
  ctx.globalAlpha = 1;
}

// drawInventory removed — moved to DOM HUD right-column (Phase 2.4)

function drawAchievementToasts(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  if (_toasts.length === 0) return;
  const now = performance.now();
  // Drop expired
  for (let i = _toasts.length - 1; i >= 0; i--) {
    if (now - _toasts[i].startMs > TOAST_DURATION_MS) _toasts.splice(i, 1);
  }
  if (_toasts.length === 0) return;

  const toastW = 280;
  const toastH = 56;
  const pad = 10;
  // Bottom-center: away from top-bar (priest / EN / 🔊) and right-column
  // (Дары / Статистика), both of which the previous top-right placement
  // used to collide with.
  const x0 = Math.round((width - toastW) / 2);
  const yBase = height - (pad + toastH);
  for (let i = 0; i < _toasts.length; i++) {
    const t = _toasts[i];
    const age = now - t.startMs;
    // Fade in first 200ms, fade out last 500ms
    const alpha = age < 200 ? age / 200
                : age > TOAST_DURATION_MS - 500 ? (TOAST_DURATION_MS - age) / 500
                : 1;
    // Stack upward; slide-in from below while fading in
    const y = yBase - i * (toastH + 6) + (1 - alpha) * 20;
    ctx.save();
    ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
    // Background
    ctx.fillStyle = "rgba(10, 8, 6, 0.92)";
    ctx.fillRect(x0, y, toastW, toastH);
    ctx.strokeStyle = "#d4af37";
    ctx.lineWidth = 2;
    ctx.strokeRect(x0, y, toastW, toastH);
    // Gold star bullet
    ctx.fillStyle = "#d4af37";
    ctx.font = "bold 22px serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText("\u2605", x0 + 10, y + toastH / 2);
    // Title + subtitle
    ctx.fillStyle = "#e8dcb4";
    ctx.font = "bold 13px sans-serif";
    ctx.textBaseline = "top";
    ctx.fillText(t.title, x0 + 34, y + 8);
    if (t.subtitle) {
      ctx.fillStyle = "#8a7a5a";
      ctx.font = "11px sans-serif";
      ctx.fillText(t.subtitle, x0 + 34, y + 28);
    }
    ctx.restore();
  }
  // Schedule re-render while toasts alive
  if (_toasts.length > 0 && typeof requestAnimationFrame !== "undefined") {
    // Fire-and-forget rAF so fade animates even between game ticks
    requestAnimationFrame(() => {
      // Note: adapter re-renders via game tick / setHoverCell path; the RAF
      // here just keeps the canvas lively. Consumers can also call update()
      // directly; we don't loop-nest adapter.update to avoid tight loops.
    });
  }
}

// ================================================================
//  MAP SCENE
// ================================================================

function renderMapScene(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  state: GameStateView,
  campaign: CampaignConfig
): void {
  // Full-resolution parchment background.
  if (mapBackgroundImg && mapBackgroundImg.naturalWidth > 0) {
    ctx.drawImage(mapBackgroundImg, 0, 0, width, height);
  }

  // Draw campaign map decorations from the campaign manifest.
  for (const [index, decoration] of campaign.visual.map.decorations.entries()) {
    const decorationImg = mapDecorationImgs[index] ?? null;
    if (!decorationImg || !decorationImg.complete) continue;
    const dw = decorationImg.naturalWidth * decoration.scale;
    const dh = decorationImg.naturalHeight * decoration.scale;
    ctx.drawImage(decorationImg, decoration.x, decoration.y, dw, dh);
  }

  // Title (only shown when no parchment loaded as fallback)
  if (!mapBackgroundImg || mapBackgroundImg.naturalWidth === 0) {
    ctx.fillStyle = TEXT_PRI;
    ctx.font = "bold 22px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText(campaign.title, width / 2, 12);
  }

  // Mage XP widget, Skills/Stats buttons moved to DOM HUD (Phase 3).

  if (!state.map?.fieldIds?.length) return;

  const cols = state.map.cols ?? campaign.map.cols ?? MAP_COLS;
  const fieldCount = state.map.fieldIds.length;
  const rows = state.map.rows ?? Math.min(campaign.map.rows ?? MAP_ROWS, Math.ceil(fieldCount / cols));
  const startingFieldId = state.map.startingFieldId ?? campaign.map.startingFieldId;
  const playableFieldIds = state.map.playableFieldIds
    ?? campaign.map.fieldIds.filter((fieldId) => campaign.map.isFieldPlayable(fieldId));

  const layout = getMapGridLayout(width, height);

  // Cell borders drawn per-cell inside drawMapCells (skips ocean) so the
  // grid follows the continent outline instead of a full rectangle.
  drawMapCells(
    ctx,
    state.map.fieldIds,
    state.map.mapProgress ?? null,
    state.map.selectedFieldId ?? null,
    startingFieldId,
    playableFieldIds,
    rows,
    cols,
    layout
  );

  // Skills and stats panels moved to DOM HUD (Phase 3).
}

/** Pointy-top hex path centered at (cx, cy); first vertex at top (canvas coords). */
function addMapHexPath(ctx: CanvasRenderingContext2D, cx: number, cy: number, R: number): void {
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const ang = -Math.PI / 2 + (Math.PI / 3) * i;
    const px = cx + R * Math.cos(ang);
    const py = cy + R * Math.sin(ang);
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
}

function drawMapCells(
  ctx: CanvasRenderingContext2D,
  fieldIds: readonly string[],
  progress: MapProgressView | null,
  selectedFieldId: string | null,
  startingFieldId: string,
  playableFieldIds: readonly string[],
  rows: number,
  cols: number,
  layout: GridLayout
): void {
  const unlocked = progress?.unlockedFieldIds
    ? new Set(progress.unlockedFieldIds)
    : null;
  const completed = progress?.completedFieldIds
    ? new Set(progress.completedFieldIds)
    : null;

  const { cellW, cellH } = layout;
  const R = getMapHexCircumradius(layout);
  const prevLineJoin = ctx.lineJoin;
  ctx.lineJoin = "round";
  const playable = new Set(playableFieldIds);

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const i = r * cols + c;
      if (i >= fieldIds.length) break;
      const fid = fieldIds[i];
      if (!playable.has(fid)) continue;
      const { x, y } = cellPos(r, c, layout);

      const isCompleted = completed?.has(fid) ?? false;
      const isUnlocked = unlocked?.has(fid) ?? unlocked === null;
      const isStart = fid === startingFieldId;
      const isSelected = fid === selectedFieldId;

      // Cell fill — pointy-top hex (matches pointToMapCell hit test)
      const hasParchment = !!(mapBackgroundImg && mapBackgroundImg.complete);
      if (isCompleted) {
        ctx.fillStyle = hasParchment ? "rgba(34, 120, 68, 0.24)" : "rgba(34, 120, 68, 0.32)";
      } else if (isStart) {
        ctx.fillStyle = hasParchment ? "rgba(255, 170, 0, 0.14)" : "rgba(255, 170, 0, 0.22)";
      } else if (isUnlocked) {
        ctx.fillStyle = hasParchment ? "rgba(100, 120, 180, 0.11)" : "rgba(100, 120, 180, 0.19)";
      } else {
        ctx.fillStyle = hasParchment ? "rgba(30, 30, 50, 0.26)" : "rgba(30, 30, 50, 0.38)";
      }
      addMapHexPath(ctx, x, y, R);
      ctx.fill();

      if (isSelected) {
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 2;
      } else if (isCompleted) {
        ctx.strokeStyle = "#44aa77";
        ctx.lineWidth = 1.5;
      } else if (isStart) {
        ctx.strokeStyle = "#ffaa00";
        ctx.lineWidth = 1.5;
      } else if (isUnlocked) {
        ctx.strokeStyle = "#556688";
        ctx.lineWidth = 1;
      } else {
        ctx.strokeStyle = "#222233";
        ctx.lineWidth = 0.5;
      }
      addMapHexPath(ctx, x, y, R);
      ctx.stroke();

      // Label
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const fontRef = Math.min(cellW, cellH);
      if (isCompleted) {
        ctx.fillStyle = "#66cc88";
        ctx.font = `${Math.max(7, Math.floor(fontRef * 0.28))}px sans-serif`;
        ctx.fillText(fid, x, y - cellH * 0.1);
        ctx.fillStyle = "#ffd700";
        ctx.font = `${Math.max(8, Math.floor(fontRef * 0.35))}px sans-serif`;
        ctx.fillText("\u2605", x, y + cellH * 0.18);
      } else if (!isUnlocked && unlocked !== null) {
        ctx.fillStyle = "#333344";
        ctx.font = `${Math.max(7, Math.floor(fontRef * 0.28))}px sans-serif`;
        ctx.fillText(fid, x, y);
      } else {
        ctx.fillStyle = isStart ? "#ffcc44" : "#bbccdd";
        ctx.font = `${Math.max(7, Math.floor(fontRef * 0.3))}px sans-serif`;
        ctx.fillText(fid, x, y);
      }
    }
  }

  ctx.lineJoin = prevLineJoin;
}

// ================================================================
//  EDITOR SCENE
// ================================================================

/**
 * Editor grid layout: use the full canvas area minus top bar for the header text.
 */
const EDITOR_HEADER_H = 40;

function getEditorGridLayout(rows: number, cols: number, canvasWidth: number, canvasHeight: number): import("./grid-math.js").GridLayout {
  const availW = canvasWidth;
  const availH = canvasHeight - EDITOR_HEADER_H;
  const cellSize = Math.min(availW / cols, availH / rows);
  const offsetX = (availW - cols * cellSize) / 2;
  const offsetY = EDITOR_HEADER_H + (availH - rows * cellSize) / 2;
  return { cellW: cellSize, cellH: cellSize, cellSize, offsetX, offsetY };
}

/**
 * Hit-test: canvas (x, y) → Cell under cursor, or null.
 * Used by input-adapter in editor scene.
 */
export function hitTestEditorCell(
  canvasX: number,
  canvasY: number,
  rows: number,
  cols: number,
  canvasWidth: number,
  canvasHeight: number
): import("../../domain/types.js").Cell | null {
  const layout = getEditorGridLayout(rows, cols, canvasWidth, canvasHeight);
  const col = Math.floor((canvasX - layout.offsetX) / layout.cellSize);
  const row = Math.floor((canvasY - layout.offsetY) / layout.cellSize);
  if (row >= 0 && row < rows && col >= 0 && col < cols) return { row, col };
  return null;
}

function renderEditorScene(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  state: import("../../application/ports.js").GameStateView
): void {
  const ed = state.editor;
  if (!ed) {
    ctx.fillStyle = TEXT_PRI;
    ctx.font = "16px sans-serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText("Editor: нет данных", 20, 30);
    return;
  }

  const { rows, cols, waypoints, blockedCells, hoverCell, mode } = ed;
  const layout = getEditorGridLayout(rows, cols, width, height);
  const { cellSize, offsetX, offsetY } = layout;

  // --- Header text ---
  ctx.fillStyle = TEXT_PRI;
  ctx.font = "bold 14px sans-serif";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  const modeLabel = mode === "waypoint" ? "путь [W]" : "блок [B]";
  ctx.fillText(
    `Редактор ${ed.fieldId} | Волн: ${ed.wavesNum} | Мобов/волна: ${ed.monstersPerWave} | HP: ${ed.monsterBaseHp} | Режим: ${modeLabel}`,
    10,
    EDITOR_HEADER_H / 2
  );

  // --- Grid backdrop ---
  ctx.fillStyle = "#1a1612";
  ctx.fillRect(offsetX, offsetY, cols * cellSize, rows * cellSize);

  // --- Blocked cells ---
  for (const bc of blockedCells) {
    ctx.fillStyle = "#3a3430";
    ctx.fillRect(
      offsetX + bc.col * cellSize,
      offsetY + bc.row * cellSize,
      cellSize,
      cellSize
    );
  }

  // --- Grid lines ---
  ctx.strokeStyle = "rgba(96, 78, 64, 0.35)";
  ctx.lineWidth = 0.5;
  for (let r = 0; r <= rows; r++) {
    ctx.beginPath();
    ctx.moveTo(offsetX, offsetY + r * cellSize);
    ctx.lineTo(offsetX + cols * cellSize, offsetY + r * cellSize);
    ctx.stroke();
  }
  for (let c = 0; c <= cols; c++) {
    ctx.beginPath();
    ctx.moveTo(offsetX + c * cellSize, offsetY);
    ctx.lineTo(offsetX + c * cellSize, offsetY + rows * cellSize);
    ctx.stroke();
  }

  // --- Waypoint path lines ---
  if (waypoints.length >= 2) {
    ctx.beginPath();
    ctx.strokeStyle = "rgba(255, 200, 50, 0.6)";
    ctx.lineWidth = 2;
    for (let i = 0; i < waypoints.length; i++) {
      const wp = waypoints[i];
      const cx = offsetX + (wp.col + 0.5) * cellSize;
      const cy = offsetY + (wp.row + 0.5) * cellSize;
      if (i === 0) ctx.moveTo(cx, cy);
      else ctx.lineTo(cx, cy);
    }
    ctx.stroke();
  }

  // --- Waypoint circles with numbers ---
  for (let i = 0; i < waypoints.length; i++) {
    const wp = waypoints[i];
    const cx = offsetX + (wp.col + 0.5) * cellSize;
    const cy = offsetY + (wp.row + 0.5) * cellSize;
    const r = Math.max(4, cellSize * 0.28);

    // Circle fill: start=green, end=red, others=gold
    if (i === 0) {
      ctx.fillStyle = "#22aa44";
    } else if (i === waypoints.length - 1) {
      ctx.fillStyle = "#cc2222";
    } else {
      ctx.fillStyle = "#d4af37";
    }
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 1;
    ctx.stroke();

    // Number
    ctx.fillStyle = "#ffffff";
    ctx.font = `bold ${Math.max(8, Math.floor(r * 1.1))}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(i + 1), cx, cy);
  }

  // --- Hover cell outline ---
  if (hoverCell) {
    ctx.strokeStyle = "#ffff00";
    ctx.lineWidth = 2;
    ctx.strokeRect(
      offsetX + hoverCell.col * cellSize + 1,
      offsetY + hoverCell.row * cellSize + 1,
      cellSize - 2,
      cellSize - 2
    );
  }

  // --- Hint footer ---
  ctx.fillStyle = TEXT_SEC;
  ctx.font = "11px sans-serif";
  ctx.textAlign = "left";
  ctx.textBaseline = "bottom";
  ctx.fillText(
    "W=путь  B=блок  C=очистить  Z=убрать посл.  S=сохранить  Esc=отмена",
    10,
    height - 4
  );
}

// --- Thematic Relic Icons ---

// --- Unified Relic Shapes (B13: shape -> grade, color -> type) ---

function drawRelicIcon(
  ctx: CanvasRenderingContext2D,
  type: string,
  level: number,
  x: number,
  y: number,
  radius: number,
  color: string,
  secondaryType?: string
): void {
  ctx.save();
  ctx.translate(x, y);

  const mixedColor = getGemColor(type, secondaryType);
  ctx.fillStyle = mixedColor;
  ctx.strokeStyle = level >= 3 ? "rgba(255,255,220,0.9)" : "rgba(255,255,255,0.6)";
  ctx.lineWidth = level >= 3 ? 2 : 1.5;

  ctx.beginPath();
  const lvl = Math.max(1, level);
  switch (lvl) {
    case 1: // Square
      ctx.rect(-radius, -radius, radius * 2, radius * 2);
      break;
    case 2: // Diamond
      ctx.moveTo(0, -radius * 1.3);
      ctx.lineTo(radius * 1.3, 0);
      ctx.lineTo(0, radius * 1.3);
      ctx.lineTo(-radius * 1.3, 0);
      ctx.closePath();
      break;
    case 3: // Circle
      ctx.arc(0, 0, radius, 0, Math.PI * 2);
      break;
    case 4: // Hexagon
      for (let i = 0; i < 6; i++) {
        const a = (i * Math.PI * 2) / 6 - Math.PI / 2;
        const px = Math.cos(a) * radius * 1.2;
        const py = Math.sin(a) * radius * 1.2;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      break;
    case 5: // 4-point star
      _pathStar(ctx, 4, radius * 1.4, radius * 0.5);
      break;
    case 6: // 6-point star
      _pathStar(ctx, 6, radius * 1.4, radius * 0.6);
      break;
    case 7: // 8-point star
      _pathStar(ctx, 8, radius * 1.4, radius * 0.7);
      break;
    case 8: // Octagon
      for (let i = 0; i < 8; i++) {
        const a = (i * Math.PI * 2) / 8 - Math.PI / 8;
        const px = Math.cos(a) * radius * 1.3;
        const py = Math.sin(a) * radius * 1.3;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      break;
    default: // lvl 9+ complex star
      _pathStar(ctx, 12, radius * 1.5, radius * 0.8);
      break;
  }
  ctx.fill();
  ctx.stroke();

  // Highlight center for level 4+
  if (lvl >= 4) {
    ctx.fillStyle = "rgba(255,255,255,0.3)";
    ctx.beginPath();
    ctx.arc(0, 0, radius * 0.4, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

function _pathStar(ctx: CanvasRenderingContext2D, spikes: number, rOut: number, rIn: number) {
  let rot = (Math.PI / 2) * 3;
  const step = Math.PI / spikes;
  ctx.moveTo(0, -rOut);
  for (let i = 0; i < spikes; i++) {
    ctx.lineTo(Math.cos(rot) * rOut, Math.sin(rot) * rOut);
    rot += step;
    ctx.lineTo(Math.cos(rot) * rIn, Math.sin(rot) * rIn);
    rot += step;
  }
  ctx.closePath();
}

// ================================================================
//  STORY SCENE
// ================================================================

function renderStoryScene(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  state: GameStateView,
  visual: CampaignVisualConfig
): void {
  const story = state.story;
  if (!story) return;

  const panel = story.panels[story.currentPanelIndex];
  if (!panel) return;

  // Background
  ctx.fillStyle = "#0a0806";
  ctx.fillRect(0, 0, width, height);

  // Image (Comic Panel)
  const imgSrc = getStoryImageSrc(panel.imageSrc, visual);
  const img = storyImgs.get(imgSrc) ?? null;

  if (img && img.complete) {
    const scale = Math.min((width * 0.8) / img.naturalWidth, (height * 0.6) / img.naturalHeight);
    const dw = img.naturalWidth * scale;
    const dh = img.naturalHeight * scale;
    const dx = (width - dw) / 2;
    const dy = (height - dh) / 2 - 50;

    ctx.drawImage(img, dx, dy, dw, dh);

    // Border
    ctx.strokeStyle = "#3a2820";
    ctx.lineWidth = 4;
    ctx.strokeRect(dx, dy, dw, dh);
  } else {
    // Placeholder for missing image
    ctx.fillStyle = "#1a1612";
    ctx.fillRect(width * 0.1, height * 0.1, width * 0.8, height * 0.5);
    ctx.strokeStyle = "#3a2820";
    ctx.strokeRect(width * 0.1, height * 0.1, width * 0.8, height * 0.5);
  }

  // Text Box (Parchment style)
  const boxY = height * 0.7;
  const boxH = height * 0.25;
  const boxX = width * 0.05;
  const boxW = width * 0.9;

  ctx.fillStyle = "#e8dcb4"; // Parchment
  ctx.fillRect(boxX, boxY, boxW, boxH);
  ctx.strokeStyle = "#3a2820";
  ctx.lineWidth = 2;
  ctx.strokeRect(boxX, boxY, boxW, boxH);

  // Text
  ctx.fillStyle = "#1a1612"; // Ink
  ctx.font = "24px 'Crimson Text', serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  // Wrap text
  const words = panel.text.split(" ");
  let line = "";
  const lines = [];
  const maxWidth = boxW * 0.9;
  for (const word of words) {
    const testLine = line + word + " ";
    if (ctx.measureText(testLine).width > maxWidth && line !== "") {
      lines.push(line);
      line = word + " ";
    } else {
      line = testLine;
    }
  }
  lines.push(line);

  lines.forEach((l, i) => {
    ctx.fillText(l, width / 2, boxY + (boxH / (lines.length + 1)) * (i + 1));
  });

  // \"Click to continue\" prompt
  ctx.fillStyle = "rgba(232, 220, 180, 0.6)";
  ctx.font = "italic 16px sans-serif";
  ctx.textAlign = "right";
  ctx.fillText(t("story.clickToContinue"), width * 0.93, height * 0.96);
}


